import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { fetchWithAuth } from "../../utils/api";
import Table from "../ui/Table";
import Modal from "../ui/Modal";
import Loader from "../ui/Loader";
import PerformanceReport from "../ui/PerformanceReport";
import Report from "../pages/Instructor/pageUI/Results/Report";
import { createRoot } from "react-dom/client";
import { swalConfirm, swalSuccess, swalError } from "../../utils/swalAlert";
import "../../App.css";

// ---------------------------------------------------------
// Reusable Dashboard Component
// ---------------------------------------------------------
// Props:
// - role: 'admin' | 'instructor' | 'student' (controls default endpoints)
// - instituteId: string (required)
// - actorId: string (instructorId or studentId for role-specific fetch; optional)
// - fetchDashboardDataProp?: async ({ instituteId, periodId, role, actorId }) => data
// - fetchPeriodsProp?: async ({ instituteId, role, actorId }) => periods[]
// - initialFilters?: { period, level, testType, classId, instructorId, testId, student }
// - disableFilters?: { period?:bool, level?:bool, class?:bool, testType?:bool, test?:bool, instructor?:bool, student?:bool }
// - pageSize?: number (table page size)
// - onError?: (err) => void
// - onLoadingChange?: (loading:boolean) => void
// - serverBaseUrl?: override base server url
//
// Data shape expectations identical to previous AdminDashboard implementation.
// ---------------------------------------------------------

const envServerUrl = `${process.env.REACT_APP_SERVER_URL || ""}`;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const formatNumber = (num) => {
  if (typeof num !== "number") return num;
  return Math.round(num * 10) / 10;
};

const defaultFetchPeriods = async ({
  instituteId,
  role,
  actorId,
  serverUrl,
}) => {
  // Periods are always by institute
  const url = `${serverUrl}/api/period/get-all/${instituteId}`;
  const res = await fetchWithAuth(url);
  if (!res.ok) throw new Error("Failed to load periods");
  return (await res.json()) || [];
};

const defaultFetchDashboardData = async ({
  instituteId,
  periodId,
  role,
  actorId,
  serverUrl,
}) => {
  // Endpoint patterns (adjust here if backend differs)
  // Admin:       /api/institute/get-full-data-by-period/:instituteId(/:periodId?)
  // Instructor:  /api/institute/get-full-data-by-period/:instituteId/:periodId?/:instructorId
  // Student (fallback same as admin unless backend requires change)
  // When periodId is "all" or empty, send "null" to get all data regardless of period
  let url = "";
  const effectivePeriodId = periodId && periodId !== "all" ? periodId : "null";

  if (role === "instructor" && actorId) {
    url = `${serverUrl}/api/institute/get-full-data-by-period/${instituteId}/${effectivePeriodId}/${role}/${actorId}`;
  } else if (role === "student" && actorId) {
    // If there is a dedicated student endpoint, replace below accordingly
    url = `${serverUrl}/api/institute/get-full-data-by-period/${instituteId}/${effectivePeriodId}/${role}/${actorId}`;
  } else {
    // admin / generic
    url = `${serverUrl}/api/institute/get-full-data-by-period/${instituteId}/${effectivePeriodId}`;
  }
  const res = await fetchWithAuth(url);
  if (!res.ok) throw new Error("Failed to load dashboard data");
  return (await res.json()) || { classes: [] };
};

// Sanitize incoming dashboard data to remove null entries and normalize arrays
const sanitizeDashboardData = (raw) => {
  if (!raw) return { classes: [] };
  const classes = (raw.classes || []).filter(Boolean).map((cls) => {
    const students = (cls.students || []).filter(Boolean).map((s) => ({
      _id: s._id,
      firstName: s.firstName || "",
      lastName: s.lastName || "",
      email: s.email || "",
      referenceId: s.referenceId || s._id || "", // Add referenceId from backend
    }));
    const tests = (cls.tests || []).filter(Boolean).map((t) => ({
      ...t,
      _id: t._id,
      testName: t.testName || "",
      testType: t.testType || "",
      maxScore: typeof t.maxScore === "number" ? t.maxScore : 0,
      answerPapers: (t.answerPapers || []).filter(Boolean).map((p) => ({
        _id: p._id,
        studentId: p.studentId != null ? p.studentId : null,
        score: p.score != null ? p.score : null,
        submittedAt: p.submittedAt || null,
        // Add the new fields from backend
        firstName: p.firstName || "",
        lastName: p.lastName || "",
        referenceId: p.referenceId || p.studentId || "",
      })),
    }));
    return { ...cls, students, tests };
  });
  return { ...raw, classes };
};

const Dashboard = ({
  role = "admin",
  instituteId,
  actorId = null,
  fetchDashboardDataProp,
  fetchPeriodsProp,
  initialFilters = {},
  disableFilters = {},
  onError,
  onLoadingChange,
  serverBaseUrl,
}) => {
  const serverUrl = serverBaseUrl || envServerUrl;
  const [dashboardData, setDashboardData] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeAction, setActiveAction] = useState({ id: null, type: null });

  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadPhase, setDownloadPhase] = useState("");
  const [downloadProgress, setDownloadProgress] = useState("");
  const [currentStudentName, setCurrentStudentName] = useState("");
  const [downloadWithAnswerPaper, setDownloadWithAnswerPaper] = useState(false);
  const [instituteInfo, setInstituteInfo] = useState(null);

  const [questionStats, setQuestionStats] = useState(null);
  const [loadingQuestionStats, setLoadingQuestionStats] = useState(false);

  const baseFilterState = {
    period: "",
    level: "all",
    testType: "all",
    classId: "all",
    instructorId: "all",
    testId: "all",
    student: "all",
  };
  const [filters, setFilters] = useState({
    ...baseFilterState,
    ...initialFilters,
  });

  const tableRef = useRef(null);

  const wrappedSetLoading = useCallback(
    (val) => {
      setLoading(val);
      onLoadingChange && onLoadingChange(val);
    },
    [onLoadingChange]
  );

  useEffect(() => {
    const fetchInstitute = async () => {
      try {
        if (!instituteId) return;
        const resp = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${instituteId}`,
          { credentials: "include" }
        );
        if (!resp) return;
        const json = await resp.json();
        if (resp.ok) {
          setInstituteInfo(json);
        }
      } catch (err) {
        console.warn("Failed to fetch institute info", err);
      }
    };
    fetchInstitute();
  }, [instituteId, serverUrl]);

  const fetchDashboardData = useCallback(
    async (periodId) => {
      try {
        wrappedSetLoading(true);
        setError(null);
        const data = await (
          fetchDashboardDataProp || defaultFetchDashboardData
        )({ instituteId, periodId, role, actorId, serverUrl });
        setDashboardData(sanitizeDashboardData(data || { classes: [] }));
      } catch (err) {
        const msg = `Failed to fetch dashboard data: ${err.message}`;
        setError(msg);
        onError && onError(err);
        setDashboardData(sanitizeDashboardData({ classes: [] }));
      } finally {
        wrappedSetLoading(false);
      }
    },
    [
      instituteId,
      role,
      actorId,
      fetchDashboardDataProp,
      serverUrl,
      onError,
      wrappedSetLoading,
    ]
  );

  const fetchPeriods = useCallback(async () => {
    try {
      const periodsData = await (fetchPeriodsProp || defaultFetchPeriods)({
        instituteId,
        role,
        actorId,
        serverUrl,
      });
      setPeriods(periodsData);
      if (periodsData && periodsData.length > 0) {
        const ongoing = periodsData.find((p) => p.status === "Ongoing");
        setFilters((prev) => ({
          ...prev,
          period: prev.period
            ? prev.period
            : ongoing
            ? ongoing._id
            : periodsData[0]._id,
        }));
      } else {
        // No periods available, set to "all" and fetch data
        setFilters((prev) => ({
          ...prev,
          period: "all",
        }));
        fetchDashboardData("all");
      }
    } catch (err) {
      console.error("Failed to fetch periods:", err);
      setError("Failed to fetch academic periods.");
      onError && onError(err);
      // Still try to fetch data with "all" period
      setFilters((prev) => ({
        ...prev,
        period: "all",
      }));
      fetchDashboardData("all");
    }
  }, [
    instituteId,
    role,
    actorId,
    fetchPeriodsProp,
    fetchDashboardData,
    serverUrl,
    onError,
  ]);

  useEffect(() => {
    if (!instituteId) return;
    fetchPeriods();
  }, [instituteId, fetchPeriods]);

  useEffect(() => {
    // Fetch data when period changes (including "all" for all periods)
    if (filters.period) {
      fetchDashboardData(filters.period);
    }
  }, [filters.period, fetchDashboardData]);

  // AUTO-SELECT class and level when a test is chosen
  useEffect(() => {
    if (
      filters.testId === "all" ||
      !dashboardData ||
      !Array.isArray(dashboardData.classes)
    )
      return;

    let foundClass = null;
    if (filters.student !== "all") {
      foundClass = (dashboardData.classes || []).find((cls) =>
        (cls.tests || []).some((t) => t._id === filters.testId)
      );
    } else {
      for (const cls of dashboardData.classes) {
        if ((cls.tests || []).some((t) => t._id === filters.testId)) {
          foundClass = cls;
          break;
        }
      }
    }
    if (!foundClass) return;

    setFilters((prev) => {
      const next = { ...prev };
      let changed = false;
      if (prev.classId !== foundClass._id) {
        next.classId = foundClass._id;
        changed = true;
      }
      if (prev.level !== foundClass.level && foundClass.level) {
        next.level = foundClass.level;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [filters.testId, filters.student, dashboardData]);

  useEffect(() => {
    if (
      filters.testType === "all" ||
      filters.classId === "all" ||
      !dashboardData?.classes
    )
      return;
    const cls = dashboardData.classes.find((c) => c._id === filters.classId);
    if (
      !cls ||
      !(cls.tests || []).some((t) => t.testType === filters.testType)
    ) {
      setFilters((prev) => ({
        ...prev,
        classId: "all",
        testId: "all",
      }));
    }
  }, [filters.testType, filters.classId, dashboardData]);

  const students = useMemo(() => {
    if (!dashboardData || !Array.isArray(dashboardData.classes)) return [];
    const map = new Map();
    dashboardData.classes.forEach((cls) => {
      (cls.students || []).forEach((student) => {
        if (student && student._id && !map.has(student._id)) {
          map.set(student._id, {
            _id: student._id,
            firstName: student.firstName,
            lastName: student.lastName,
            email: student.email,
            referenceId: student.referenceId || student._id || "", // Add referenceId
          });
        }
      });
    });
    return Array.from(map.values());
  }, [dashboardData]);

  const periodDisplayMap = useMemo(() => {
    const map = {};
    // Sort periods by term and year for consistent display
    const sortedPeriods = (periods || []).sort((a, b) => {
      // First sort by year (descending - newest first)
      const yearA = parseInt(a.year) || 0;
      const yearB = parseInt(b.year) || 0;
      if (yearA !== yearB) {
        return yearB - yearA;
      }

      // Then sort by term alphabetically
      return (a.term || "").localeCompare(b.term || "");
    });

    sortedPeriods.forEach((p) => {
      if (p && p._id) {
        map[p._id] = `${p.term}-${p.year}`;
      }
    });
    return map;
  }, [periods]);

  const studentAvailable = useMemo(() => {
    if (!dashboardData || !Array.isArray(dashboardData.classes))
      return { classes: [], tests: [] };
    if (!filters.student || filters.student === "all")
      return { classes: [], tests: [] };

    const classesWithStudent = dashboardData.classes.filter((cls) =>
      (cls.students || []).some((s) => s._id === filters.student)
    );

    const tests = [];
    const seen = new Set();
    classesWithStudent.forEach((cls) => {
      (cls.tests || []).forEach((t) => {
        if (!seen.has(t._id)) {
          if (
            filters.testType === "all" ||
            (t.testType && t.testType === filters.testType)
          ) {
            seen.add(t._id);
            tests.push({
              ...t,
              _id: t._id,
              testName: t.testName,
              testType: t.testType,
              classId: cls._id,
              className: cls.name,
              classLevel: cls.level,
              period: cls.period,
            });
          }
        }
      });
    });

    return { classes: classesWithStudent, tests };
  }, [dashboardData, filters.student, filters.testType]);

  const filterOptions = useMemo(() => {
    if (!dashboardData || !Array.isArray(dashboardData.classes))
      return {
        periodIds: [],
        levels: [],
        testTypes: [],
        classNames: [],
        instructors: [],
        testNames: [],
        students: [],
      };

    let candidateClasses = dashboardData.classes.slice();

    if (filters.level !== "all") {
      candidateClasses = candidateClasses.filter(
        (c) => c.level === filters.level
      );
    }
    if (filters.classId !== "all") {
      candidateClasses = candidateClasses.filter(
        (c) => c._id === filters.classId
      );
    }
    if (filters.instructorId !== "all") {
      candidateClasses = candidateClasses.filter(
        (c) => c.instructor && c.instructor._id === filters.instructorId
      );
    }

    if (filters.student !== "all") {
      candidateClasses = candidateClasses.filter((c) =>
        (c.students || []).some((s) => s._id === filters.student)
      );
    }

    const testTypes = [
      ...new Set(
        candidateClasses
          .flatMap((c) => c.tests || [])
          .map((t) => t?.testType)
          .filter(Boolean)
      ),
    ].sort((a, b) => a.localeCompare(b)); // Sort alphabetically

    let classesForTestType = candidateClasses;
    if (filters.testType !== "all") {
      classesForTestType = classesForTestType.filter((c) =>
        (c.tests || []).some((t) => t.testType === filters.testType)
      );
    }

    const classNames = [
      ...new Map(
        classesForTestType.map((c) => [
          c._id,
          { id: c._id, name: c.name, level: c.level, period: c.period },
        ])
      ).values(),
    ].sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by name

    const instructors = [
      ...new Map(
        candidateClasses
          .filter((c) => c.instructor)
          .map((c) => [
            c.instructor._id,
            {
              id: c.instructor._id,
              name: `${c.instructor.firstName} ${c.instructor.lastName}`,
            },
          ])
      ).values(),
    ].sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by name

    let testSourceClasses = classesForTestType;
    if (filters.student !== "all") {
      testSourceClasses = classesForTestType;
    }

    // Natural sorting function for test names
    const naturalSort = (a, b) => {
      const normalize = (str) => {
        return str.replace(/(\d+)/g, (match) => match.padStart(10, "0"));
      };
      return normalize(a).localeCompare(normalize(b));
    };

    const testNames = [
      ...new Map(
        testSourceClasses
          .flatMap((c) => c.tests || [])
          .filter(
            (t) =>
              (filters.testType === "all" ||
                (t.testType && t.testType === filters.testType)) &&
              (filters.student === "all" || true)
          )
          .map((t) => [t._id, { id: t._id, name: t.testName }])
      ).values(),
    ].sort((a, b) => naturalSort(a.name, b.name)); // Use natural sorting for test names

    let studentsPoolClasses = candidateClasses;
    if (filters.testType !== "all") {
      studentsPoolClasses = candidateClasses.filter((c) =>
        (c.tests || []).some((t) => t.testType === filters.testType)
      );
    }

    const studentMap = new Map();
    studentsPoolClasses.forEach((c) => {
      (c.students || []).forEach((s) => {
        if (s && s._id) {
          if (filters.testType !== "all") {
            const hasTestTypeParticipation = (c.tests || []).some(
              (t) => t.testType === filters.testType
            );
            if (!hasTestTypeParticipation) return;
          }
          // Use _id as unique key and include referenceId for display
          if (!studentMap.has(s._id)) {
            studentMap.set(s._id, {
              _id: s._id,
              firstName: s.firstName,
              lastName: s.lastName,
              email: s.email,
              referenceId: s.referenceId || s._id || "", // Add referenceId
            });
          }
        }
      });
    });

    const periodIds = [
      ...new Set(
        (dashboardData.classes || []).map((c) => c.period).filter(Boolean)
      ),
    ];

    // Helper function to sort levels - handles Grade 1-12, Bachelor's, Master's etc.
    const sortLevels = (levels) => {
      return levels.sort((a, b) => {
        // Extract grade numbers from strings like "Grade 1", "Grade 2", etc.
        const extractGradeNumber = (level) => {
          const gradeMatch = level.match(/grade\s+(\d+)/i);
          return gradeMatch ? parseInt(gradeMatch[1], 10) : null;
        };

        const aGrade = extractGradeNumber(a);
        const bGrade = extractGradeNumber(b);

        // If both are grades, sort by grade number
        if (aGrade !== null && bGrade !== null) {
          return aGrade - bGrade;
        }

        // If one is a grade and one isn't, grades come first
        if (aGrade !== null && bGrade === null) {
          return -1;
        }
        if (aGrade === null && bGrade !== null) {
          return 1;
        }

        // Handle pure numeric levels
        const aNum = parseFloat(a);
        const bNum = parseFloat(b);

        if (!isNaN(aNum) && !isNaN(bNum)) {
          return aNum - bNum;
        }

        // If one is numeric and one is text, numeric comes first
        if (!isNaN(aNum) && isNaN(bNum)) {
          return -1;
        }
        if (isNaN(aNum) && !isNaN(bNum)) {
          return 1;
        }

        // For non-grade text levels, handle specific order
        const levelOrder = {
          "bachelor's": 1,
          bachelor: 1,
          "undergraduate's": 1,
          undergraduate: 1,
          "postgraduate's": 2,
          postgraduate: 2,
          "master's": 2,
          master: 2,
          doctorate: 3,
          phd: 3,
        };

        const aOrder = levelOrder[a.toLowerCase()] || 999;
        const bOrder = levelOrder[b.toLowerCase()] || 999;

        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }

        // Fallback to alphabetical sort
        return a.localeCompare(b);
      });
    };

    const levels = sortLevels([
      ...new Set(candidateClasses.map((c) => c.level).filter(Boolean)),
    ]);

    // Sort students by full name (firstName + lastName)
    const students = Array.from(studentMap.values()).sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`.trim();
      const nameB = `${b.firstName} ${b.lastName}`.trim();
      return nameA.localeCompare(nameB);
    });

    return {
      periodIds,
      levels,
      testTypes,
      classNames,
      instructors,
      testNames,
      students,
    };
  }, [dashboardData, filters]);

  const hasActiveFilters = useMemo(
    () =>
      Object.entries(filters).some(([k, v]) => k !== "period" && v !== "all"),
    [filters]
  );

  const filteredData = useMemo(() => {
    if (!dashboardData || !Array.isArray(dashboardData.classes)) return null;

    let filteredClasses = dashboardData.classes.filter((cls) => {
      const periodMatch =
        (periods || []).length === 0 ||
        filters.period === "all" ||
        cls.period === filters.period;

      return (
        periodMatch &&
        (filters.level === "all" || cls.level === filters.level) &&
        (filters.classId === "all" || cls._id === filters.classId) &&
        (filters.instructorId === "all" ||
          (cls.instructor && cls.instructor._id === filters.instructorId))
      );
    });

    if (filters.student !== "all") {
      filteredClasses = filteredClasses.filter((cls) =>
        (cls.students || []).some((s) => s._id === filters.student)
      );
    }

    filteredClasses = filteredClasses.map((cls) => ({
      ...cls,
      tests: (cls.tests || [])
        .filter(
          (t) =>
            (filters.testType === "all" ||
              (t.testType && t.testType === filters.testType)) &&
            (filters.testId === "all" || t._id === filters.testId)
        )
        .map((t) => ({ ...t })),
    }));

    if (filters.student !== "all") {
      filteredClasses = filteredClasses.map((cls) => ({
        ...cls,
        tests: (cls.tests || []).map((test) => ({
          ...test,
          answerPapers: (test.answerPapers || []).filter(
            (paper) =>
              filters.student === "all" || paper.studentId === filters.student
          ),
        })),
      }));
    }

    return { ...dashboardData, classes: filteredClasses };
  }, [dashboardData, filters, periods]);

  // Check if Level>Class>Test filter is applied
  const isSpecificTestSelected = useMemo(() => {
    return (
      filters.level !== "all" &&
      filters.classId !== "all" &&
      filters.testId !== "all" &&
      filters.testId !== ""
    );
  }, [filters.level, filters.classId, filters.testId]);

  const fetchQuestionStats = useCallback(
    async (testId) => {
      if (!testId || testId === "all") {
        setQuestionStats(null);
        return;
      }

      try {
        setLoadingQuestionStats(true);
        const response = await fetchWithAuth(
          `${serverUrl}/api/score/getQuestionStatsForTest/${testId}`,
          { credentials: "include" }
        );

        if (response && response.ok) {
          const data = await response.json();
          setQuestionStats(data.stats || []);
        } else {
          console.warn("Failed to fetch question stats");
          setQuestionStats([]);
        }
      } catch (err) {
        console.error("Error fetching question stats:", err);
        setQuestionStats([]);
      } finally {
        setLoadingQuestionStats(false);
      }
    },
    [serverUrl]
  );

  // Add effect to fetch question stats when test is selected
  useEffect(() => {
    if (isSpecificTestSelected && filters.testId !== "all") {
      fetchQuestionStats(filters.testId);
    } else {
      setQuestionStats(null);
    }
  }, [isSpecificTestSelected, filters.testId, fetchQuestionStats]);

  useEffect(() => {
    // Auto-select test when Level > Class > TestType is applied and there's only one matching test
    if (
      filters.level !== "all" &&
      filters.classId !== "all" &&
      filters.testType !== "all" &&
      filters.testId === "all" &&
      dashboardData?.classes
    ) {
      const targetClass = dashboardData.classes.find(
        (cls) => cls._id === filters.classId
      );

      if (targetClass) {
        const matchingTests = (targetClass.tests || []).filter(
          (test) => test.testType === filters.testType
        );

        // If there's exactly one test matching the test type, auto-select it
        if (matchingTests.length === 1) {
          setFilters((prev) => ({
            ...prev,
            testId: matchingTests[0]._id,
          }));
        }
      }
    }
  }, [
    filters.level,
    filters.classId,
    filters.testType,
    filters.testId,
    dashboardData,
  ]);

  // Get graded students for the selected test
  const gradedStudentsForTest = useMemo(() => {
    if (!isSpecificTestSelected || !filteredData?.classes) return [];

    const targetClass = filteredData.classes.find(
      (c) => c._id === filters.classId
    );
    if (!targetClass) return [];

    const targetTest = targetClass.tests?.find((t) => t._id === filters.testId);
    if (!targetTest) return [];

    const gradedPapers =
      targetTest.answerPapers?.filter(
        (paper) => paper.score !== null && paper.score !== undefined
      ) || [];

    return gradedPapers.map((paper) => {
      // First try to use data directly from the answer paper (from backend)
      let firstName = paper.firstName || "";
      let lastName = paper.lastName || "";
      let referenceId = paper.referenceId || "";

      // Fallback to finding student data from class students if backend data is not available
      if (!firstName || !lastName || !referenceId) {
        const student = targetClass.students?.find(
          (s) => s._id === paper.studentId
        );
        firstName = firstName || student?.firstName || "";
        lastName = lastName || student?.lastName || "";
        referenceId =
          referenceId || student?.referenceId || paper.studentId || "";
      }

      return {
        ...paper,
        firstName,
        lastName,
        referenceId,
        testName: targetTest.testName,
        className: targetClass.name,
        classLevel: targetClass.level,
      };
    });
  }, [isSpecificTestSelected, filteredData, filters.classId, filters.testId]);

  const analytics = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData.classes)) return null;
    const classes = filteredData.classes;

    // Period can be "all" or a specific period ID - both should work for charts
    const hasPeriodFilter = filters.period && filters.period !== "";
    const isWholeInstitute = (periods || []).length === 0 && !hasPeriodFilter;
    const isOnlyPeriod =
      !isWholeInstitute &&
      hasPeriodFilter &&
      filters.level === "all" &&
      filters.classId === "all" &&
      filters.testId === "all" &&
      filters.student === "all" &&
      filters.testType === "all";
    const isPeriodAndLevel =
      hasPeriodFilter &&
      filters.level !== "all" &&
      filters.classId === "all" &&
      filters.testId === "all" &&
      filters.student === "all" &&
      filters.testType === "all";
    const isPeriodLevelAndSubject =
      hasPeriodFilter &&
      filters.level !== "all" &&
      filters.classId !== "all" &&
      filters.testId === "all" &&
      filters.student === "all" &&
      filters.testType === "all";
    const isPeriodLevelClassAndTest =
      hasPeriodFilter &&
      filters.level !== "all" &&
      filters.classId !== "all" &&
      filters.testId !== "all" &&
      filters.testId !== "not all" &&
      filters.student === "all";

    const isTestTypeOnly =
      filters.testType !== "all" &&
      filters.classId === "all" &&
      filters.student === "all" &&
      filters.testId === "all";
    const isTestTypeAndClass =
      filters.testType !== "all" &&
      filters.classId !== "all" &&
      filters.student === "all" &&
      filters.testId === "all";
    const isStudentAndTestType =
      filters.testType !== "all" &&
      filters.student !== "all" &&
      filters.testId === "all";

    const uniqueStudents = new Set();
    const uniqueInstructors = new Set();
    const uniqueSubjects = new Set();
    const uniqueLevels = new Set();
    let totalTests = 0;
    let allScores = [];

    classes.forEach((cls) => {
      if (cls.instructor && cls.instructor._id)
        uniqueInstructors.add(cls.instructor._id);
      // Count subject occurrences by classId to avoid merging different classes sharing same name
      if (cls._id) uniqueSubjects.add(cls._id);
      if (cls.level) uniqueLevels.add(cls.level);
      (cls.students || []).forEach((s) => {
        if (s && s._id) uniqueStudents.add(s._id);
      });
      totalTests += (cls.tests || []).length;
      (cls.tests || []).forEach((t) => {
        const maxScore = t.maxScore || 30;
        (t.answerPapers || []).forEach((p) => {
          if (p && p.score !== null && maxScore > 0) {
            allScores.push((parseFloat(p.score) / maxScore) * 100);
          }
        });
      });
    });

    const overallAverage =
      allScores.length > 0
        ? allScores.reduce((s, v) => s + v, 0) / allScores.length
        : 0;

    let kpiData = {};
    let chartData = {};

    if (role === "student" && actorId) {
      // Student view: show their performance across subjects and tests
      let totalScoreSum = 0;
      let totalMaxSum = 0;
      const scoresBySubject = {};
      const subjectTests = {};

      classes.forEach((cls) => {
        const subject = cls.name || "Unknown";
        (cls.tests || []).forEach((test) => {
          const maxScore = test.maxScore || 30;
          const studentPapers = (test.answerPapers || []).filter(
            (paper) => paper.studentId === actorId && paper.score !== null
          );
          if (studentPapers.length > 0) {
            const avgScore =
              studentPapers.reduce((s, p) => s + parseFloat(p.score), 0) /
              studentPapers.length;
            totalScoreSum += avgScore;
            totalMaxSum += maxScore;
            const pct = maxScore > 0 ? (avgScore / maxScore) * 100 : 0;
            if (!scoresBySubject[subject]) scoresBySubject[subject] = [];
            scoresBySubject[subject].push(pct);
            if (!subjectTests[subject]) subjectTests[subject] = [];
            subjectTests[subject].push({
              testName: test.testName,
              percentage: pct,
              testId: test._id, // Add testId for click handling
            });
          }
        });
      });

      const overallPercentage =
        totalMaxSum > 0 ? (totalScoreSum / totalMaxSum) * 100 : 0;

      const studentObj = students.find((s) => s._id === actorId);
      const studentName = studentObj
        ? `${studentObj.firstName} ${studentObj.lastName}`
        : "";

      kpiData = {
        studentId: actorId,
        totalScoreSum,
        totalMaxSum,
        overallPercentage,
        totalSubjects: Object.keys(scoresBySubject).length,
        totalTests: Object.values(subjectTests).reduce(
          (acc, arr) => acc + arr.length,
          0
        ),
        studentName,
      };

      const avgBySubject = Object.keys(scoresBySubject).map((subject) => {
        const arr = scoresBySubject[subject];
        return {
          subject,
          avgPercentage:
            arr.length > 0 ? arr.reduce((s, v) => s + v, 0) / arr.length : 0,
        };
      });

      chartData = {
        type: "studentMulti",
        avgBySubject,
        subjectTests,
      };

      return {
        kpiData,
        chartData,
        scenario: {
          isStudent: true,
          isStudentRole: true, // Add flag to distinguish student role view
          testSelected: false,
        },
      };
    }

    // Student + testType (no specific test)
    if (filters.student !== "all" && filters.testId === "all") {
      if (filters.testType !== "all" && isStudentAndTestType) {
        let totalScoreSum = 0;
        let totalMaxSum = 0;
        const scoresBySubject = {};
        const subjectTests = {};
        classes.forEach((cls) => {
          const subject = cls.name || "Unknown";
          (cls.tests || [])
            .filter(
              (t) =>
                filters.testType === "all" ||
                (t.testType && t.testType === filters.testType)
            )
            .forEach((test) => {
              const maxScore = test.maxScore || 30;
              const studentPapers = (test.answerPapers || []).filter(
                (paper) =>
                  paper.studentId === filters.student && paper.score !== null
              );
              if (studentPapers.length > 0) {
                const avgScore =
                  studentPapers.reduce((s, p) => s + parseFloat(p.score), 0) /
                  studentPapers.length;
                totalScoreSum += avgScore;
                totalMaxSum += maxScore;
                const pct = maxScore > 0 ? (avgScore / maxScore) * 100 : 0;
                if (!scoresBySubject[subject]) scoresBySubject[subject] = [];
                scoresBySubject[subject].push(pct);
                if (!subjectTests[subject]) subjectTests[subject] = [];
                subjectTests[subject].push({
                  testId: test._id,
                  testName: test.testName,
                  percentage: pct,
                });
              }
            });
        });

        const overallPercentage =
          totalMaxSum > 0 ? (totalScoreSum / totalMaxSum) * 100 : 0;

        kpiData = {
          studentId: filters.student,
          testType: filters.testType,
          totalScoreSum,
          totalMaxSum,
          overallPercentage,
          totalSubjects: Object.keys(scoresBySubject).length,
          totalTests: Object.values(subjectTests).reduce(
            (acc, arr) => acc + arr.length,
            0
          ),
          studentName:
            (students || []).find((s) => s._id === filters.student)?.firstName +
              " " +
              (students || []).find((s) => s._id === filters.student)
                ?.lastName || "",
        };

        const avgBySubject = Object.keys(scoresBySubject).map((subject) => {
          const arr = scoresBySubject[subject];
          return {
            subject,
            avgPercentage:
              arr.length > 0 ? arr.reduce((s, v) => s + v, 0) / arr.length : 0,
          };
        });

        chartData = {
          type: "studentTestTypeMulti",
          avgBySubject,
          subjectTests,
        };

        return {
          kpiData,
          chartData,
          scenario: {
            isStudentTestType: true,
          },
        };
      }
    }

    // Student analytics (all test types)
    if (filters.student !== "all" && filters.testType === "all") {
      let totalScoreSum = 0;
      let totalMaxSum = 0;
      const scoresBySubject = {};
      const subjectTests = {};
      classes.forEach((cls) => {
        const subject = cls.name || "Unknown";
        (cls.tests || []).forEach((test) => {
          const maxScore = test.maxScore || 30;
          const studentPapers = (test.answerPapers || []).filter(
            (paper) =>
              paper.studentId === filters.student && paper.score !== null
          );
          if (studentPapers.length > 0) {
            const avgScore =
              studentPapers.reduce((s, p) => s + parseFloat(p.score), 0) /
              studentPapers.length;
            totalScoreSum += avgScore;
            totalMaxSum += maxScore;
            const pct = maxScore > 0 ? (avgScore / maxScore) * 100 : 0;
            if (!scoresBySubject[subject]) scoresBySubject[subject] = [];
            scoresBySubject[subject].push(pct);
            if (!subjectTests[subject]) subjectTests[subject] = [];
            subjectTests[subject].push({
              testId: test._id,
              testName: test.testName,
              percentage: pct,
            });
          }
        });
      });

      const overallPercentage =
        totalMaxSum > 0 ? (totalScoreSum / totalMaxSum) * 100 : 0;

      kpiData = {
        studentId: filters.student,
        totalScoreSum,
        totalMaxSum,
        overallPercentage,
        totalSubjects: Object.keys(scoresBySubject).length,
        totalTests: Object.values(subjectTests).reduce(
          (acc, arr) => acc + arr.length,
          0
        ),
        studentName:
          (students || []).find((s) => s._id === filters.student)?.firstName +
            " " +
            (students || []).find((s) => s._id === filters.student)?.lastName ||
          "",
      };

      if (filters.testId !== "all") {
        const specificTest = classes
          .flatMap((c) => c.tests || [])
          .find((t) => t._id === filters.testId);
        let studentPercentage = null;
        const classmates = {};
        if (specificTest) {
          const maxScore = specificTest.maxScore || 30;
          const papers = (specificTest.answerPapers || []).filter(
            (p) => p.score !== null
          );
          papers.forEach((paper) => {
            const studentObj = classes
              .flatMap((c) => c.students || [])
              .find((s) => s._id === paper.studentId);
            const name = studentObj
              ? `${studentObj.firstName} ${studentObj.lastName}`
              : paper.studentId;
            classmates[name] = (parseFloat(paper.score) / maxScore) * 100;
            if (paper.studentId === filters.student) {
              studentPercentage = (parseFloat(paper.score) / maxScore) * 100;
            }
          });
        }
        chartData = {
          type: "studentSingleTest",
          studentPercentage,
          classmates,
          specificTestName: specificTest?.testName || "",
        };
      } else {
        const avgBySubject = Object.keys(scoresBySubject).map((subject) => {
          const arr = scoresBySubject[subject];
          return {
            subject,
            avgPercentage:
              arr.length > 0 ? arr.reduce((s, v) => s + v, 0) / arr.length : 0,
          };
        });
        chartData = {
          type: "studentMulti",
          avgBySubject,
          subjectTests,
        };
      }

      return {
        kpiData,
        chartData,
        scenario: {
          isStudent: true,
          testSelected: filters.testId !== "all",
        },
      };
    }

    // TestType only (all classes)
    if (isTestTypeOnly) {
      const avgByClass = [];
      const scoresByStudent = {};

      classes.forEach((cls) => {
        const percentages = [];
        const studentAccum = {};
        const studentCounts = {};
        (cls.tests || [])
          .filter((t) => t.testType === filters.testType)
          .forEach((t) => {
            const maxScore = t.maxScore || 30;
            (t.answerPapers || []).forEach((p) => {
              if (p.score !== null && maxScore > 0) {
                const pct = (parseFloat(p.score) / maxScore) * 100;
                percentages.push(pct);
                if (!studentAccum[p.studentId]) {
                  studentAccum[p.studentId] = 0;
                  studentCounts[p.studentId] = 0;
                }
                studentAccum[p.studentId] += pct;
                studentCounts[p.studentId] += 1;
              }
            });
          });
        const classAvg =
          percentages.length > 0
            ? percentages.reduce((s, v) => s + v, 0) / percentages.length
            : 0;
        if (percentages.length > 0) {
          avgByClass.push({ classId: cls._id, className: cls.name, avgPercentage: classAvg });
        }
        Object.keys(studentAccum).forEach((sid) => {
          const avg = studentAccum[sid] / studentCounts[sid];
          if (!scoresByStudent[sid]) scoresByStudent[sid] = [];
          scoresByStudent[sid].push(avg);
        });
      });

      const avgByStudent = Object.keys(scoresByStudent).map((sid) => {
        const studentObj = students.find((s) => s._id === sid);
        const name = studentObj
          ? `${studentObj.firstName} ${studentObj.lastName}`
          : sid;
        const arr = scoresByStudent[sid];
        return {
          studentId: sid,
          student: name,
          avgPercentage:
            arr.length > 0 ? arr.reduce((s, v) => s + v, 0) / arr.length : 0,
        };
      });

      kpiData = {
        testType: filters.testType,
        totalClasses: avgByClass.length,
        totalStudents: avgByStudent.length,
        overallAverage:
          avgByClass.length > 0
            ? avgByClass.reduce((s, v) => s + v.avgPercentage, 0) /
              avgByClass.length
            : 0,
      };

      chartData = {
        type: "testTypeAllClasses",
        avgByClass,
        avgByStudent,
      };

      return {
        kpiData,
        chartData,
        scenario: { isTestTypeOnly: true },
      };
    }

    // TestType + Class
    if (isTestTypeAndClass) {
      const targetClass = classes.find((c) => c._id === filters.classId);
      const scoresByStudent = {};
      const studentCounts = {};
      let testCount = 0;

      if (targetClass) {
        (targetClass.tests || [])
          .filter((t) => t.testType === filters.testType)
          .forEach((t) => {
            testCount += 1;
            const maxScore = t.maxScore || 30;
            (t.answerPapers || []).forEach((p) => {
              if (p.score !== null && maxScore > 0) {
                const pct = (parseFloat(p.score) / maxScore) * 100;
                if (!scoresByStudent[p.studentId]) {
                  scoresByStudent[p.studentId] = 0;
                  studentCounts[p.studentId] = 0;
                }
                scoresByStudent[p.studentId] += pct;
                studentCounts[p.studentId] += 1;
              }
            });
          });
      }

      const studentAverages = Object.keys(scoresByStudent).map((sid) => {
        const studentObj =
          (targetClass?.students || []).find((s) => s._id === sid) ||
          students.find((s) => s._id === sid);
        const name = studentObj
          ? `${studentObj.firstName} ${studentObj.lastName}`
          : sid;
        return {
          studentId: sid,
          student: name,
          avgPercentage: scoresByStudent[sid] / studentCounts[sid],
        };
      });

      const classAverage =
        studentAverages.length > 0
          ? studentAverages.reduce((s, v) => s + v.avgPercentage, 0) /
            studentAverages.length
          : 0;

      kpiData = {
        testType: filters.testType,
        className: targetClass?.name || "",
        classLevel: targetClass?.level || "",
        totalStudents: studentAverages.length,
        totalTests: testCount,
        classAverage,
      };

      chartData = {
        type: "testTypeClass",
        studentAverages,
      };

      return {
        kpiData,
        chartData,
        scenario: { isTestTypeAndClass: true },
      };
    }

    // Non-student / non-testType scenarios
    const scoresByScenario = {};
    if (isWholeInstitute || isOnlyPeriod) {
      const scoresByLevel = {};
      classes.forEach((cls) => {
        const level = cls.level || "Unknown";
        if (!scoresByLevel[level]) scoresByLevel[level] = [];
        (cls.tests || []).forEach((test) => {
          const maxScore = test.maxScore || 30;
          (test.answerPapers || []).forEach((paper) => {
            if (paper && paper.score !== null && maxScore > 0) {
              scoresByLevel[level].push(
                (parseFloat(paper.score) / maxScore) * 100
              );
            }
          });
        });
      });
      const avgByLevel = Object.keys(scoresByLevel).map((level) => ({
        level,
        avgPercentage:
          scoresByLevel[level].length > 0
            ? scoresByLevel[level].reduce((s, v) => s + v, 0) /
              scoresByLevel[level].length
            : 0,
      }));
      kpiData = {
        totalStudents: uniqueStudents.size,
        totalInstructors: uniqueInstructors.size,
        totalSubjects: uniqueSubjects.size,
        totalLevels: uniqueLevels.size,
        overallAverage,
        totalTests,
      };
      chartData = { type: "levelAnalysis", avgByLevel };
      scoresByScenario.level = avgByLevel;
    } else if (isPeriodAndLevel) {
      // Fix: Use class IDs for uniqueness instead of class names
      const scoresByClassId = {};
      const classIdToNameMap = {};

      classes.forEach((cls) => {
        const classId = cls._id;
        const className = cls.name || "Unknown";

        // Store the class name mapping
        classIdToNameMap[classId] = className;

        if (!scoresByClassId[classId]) scoresByClassId[classId] = [];

        (cls.tests || []).forEach((test) => {
          const maxScore = test.maxScore || 30;
          (test.answerPapers || []).forEach((paper) => {
            if (paper && paper.score !== null && maxScore > 0) {
              scoresByClassId[classId].push(
                (parseFloat(paper.score) / maxScore) * 100
              );
            }
          });
        });
      });

      // Create avgBySubject using class IDs for uniqueness but display names
      const avgBySubject = Object.keys(scoresByClassId).map((classId) => ({
        classId, // Store the class ID for click handling
        subject: classIdToNameMap[classId], // Display the class name
        avgPercentage:
          scoresByClassId[classId].length > 0
            ? scoresByClassId[classId].reduce((s, v) => s + v, 0) /
              scoresByClassId[classId].length
            : 0,
      }));

      kpiData = {
        totalStudents: uniqueStudents.size,
        totalInstructors: uniqueInstructors.size,
        totalSubjects: Object.keys(scoresByClassId).length, // Count unique class IDs
        totalTests,
        overallAverage,
        levelName: filters.level,
      };
      chartData = { type: "subjectAnalysis", avgBySubject };
    } else if (isPeriodLevelAndSubject) {
      const subjectClasses = classes.filter(
        (cls) => cls._id === filters.classId
      );
      const subjectPercentages = [];
      subjectClasses.forEach((cls) => {
        (cls.tests || []).forEach((test) => {
          const maxScore = test.maxScore || 30;
          (test.answerPapers || []).forEach((paper) => {
            if (paper && paper.score !== null && maxScore > 0) {
              subjectPercentages.push(
                (parseFloat(paper.score) / maxScore) * 100
              );
            }
          });
        });
      });
      const subjectAverage =
        subjectPercentages.length > 0
          ? subjectPercentages.reduce((s, v) => s + v, 0) /
            subjectPercentages.length
          : 0;
      kpiData = {
        totalStudents: uniqueStudents.size,
        totalTests,
        subjectAverage,
        subjectName:
          dashboardData?.classes?.find((c) => c._id === filters.classId)
            ?.name || "",
        levelName: filters.level,
      };
      const scoresByTest = {};
      const testNamesMap = {};
      subjectClasses.forEach((cls) => {
        (cls.tests || []).forEach((test) => {
          const maxScore = test.maxScore || 30;
          const testId = test._id;
          if (!scoresByTest[testId]) scoresByTest[testId] = [];
          testNamesMap[testId] = test.testName || "Unknown";
          (test.answerPapers || []).forEach((paper) => {
            if (paper && paper.score !== null && maxScore > 0) {
              scoresByTest[testId].push(
                (parseFloat(paper.score) / maxScore) * 100
              );
            }
          });
        });
      });
      const avgByTest = Object.keys(scoresByTest).map((testId) => ({
        testId,
        testName: testNamesMap[testId],
        avgPercentage:
          scoresByTest[testId].length > 0
            ? scoresByTest[testId].reduce((s, v) => s + v, 0) /
              scoresByTest[testId].length
            : 0,
      }));
      chartData = { type: "testAnalysis", avgByTest };
    } else if (isPeriodLevelClassAndTest) {
      const specificTest = classes
        .flatMap((cls) => cls.tests || [])
        .find((t) => t._id === filters.testId);
      const scoresByStudent = {};
      let testAverage = 0;
      if (specificTest) {
        const maxScore = specificTest.maxScore || 30;
        const studentPercentages = [];
        (specificTest.answerPapers || []).forEach((paper) => {
          const student = classes
            .flatMap((c) => c.students || [])
            .find((s) => s._id === paper.studentId);
          if (student && paper.score !== null && maxScore > 0) {
            const pct = (parseFloat(paper.score) / maxScore) * 100;
            scoresByStudent[`${student.firstName} ${student.lastName}`] = pct;
            studentPercentages.push(pct);
          }
        });
        testAverage =
          studentPercentages.length > 0
            ? studentPercentages.reduce((s, v) => s + v, 0) /
              studentPercentages.length
            : 0;
      }
      kpiData = {
        totalStudents: Object.keys(scoresByStudent).length,
        testName: specificTest?.testName || "",
        subjectName:
          dashboardData?.classes?.find((c) => c._id === filters.classId)
            ?.name || "",
        levelName: filters.level,
        testAverage,
      };
      chartData = { type: "studentScores", scoresByStudent };
    }

    return {
      kpiData,
      chartData,
      scenario: {
        isOnlyPeriod: isWholeInstitute || isOnlyPeriod,
        isPeriodAndLevel,
        isPeriodLevelAndSubject,
        isPeriodLevelClassAndTest,
        isTestTypeOnly,
        isTestTypeAndClass,
        isStudentTestType:
          filters.student !== "all" &&
          filters.testType !== "all" &&
          filters.testId === "all",
        isStudent: filters.student !== "all" && filters.testType === "all",
      },
    };
  }, [filteredData, filters, periods, dashboardData, students, actorId, role]);

  const tableData = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData.classes)) return [];
    const data = [];
    filteredData.classes.forEach((cls) => {
      (cls.tests || []).forEach((test) => {
        const maxScore = test.maxScore || 30;
        (test.answerPapers || []).forEach((paper) => {
          let student = null;
          let studentName = "";
          let studentEmail = "";
          let referenceId = "";

          // First try to use data from answer paper (from backend)
          if (paper.firstName || paper.lastName) {
            studentName = `${paper.firstName || ""} ${
              paper.lastName || ""
            }`.trim();
            referenceId = paper.referenceId || paper.studentId || "";
            studentEmail = ""; // Not available in paper object
          }

          // Fallback to finding student in class students
          if (!studentName) {
            student = (cls.students || []).find(
              (s) => s._id === paper.studentId
            );
            if (student) {
              studentName = `${student.firstName} ${student.lastName}`;
              studentEmail = student.email;
              referenceId = student.referenceId || student._id || "";
            }
          }

          if (studentName) {
            // Only add if we have student name
            data.push({
              _id: paper._id,
              studentId: referenceId, // Use referenceId instead of internal _id
              period: periodDisplayMap[cls.period] || cls.period,
              level: cls.level || "N/A",
              className: cls.name,
              classLevel: cls.level || "",
              instructor: `${cls.instructor?.firstName || ""} ${
                cls.instructor?.lastName || ""
              }`.trim(),
              testType: test.testType || "N/A",
              testName: test.testName,
              studentName,
              studentEmail: studentEmail,
              score:
                paper.score !== null ? parseFloat(paper.score) : "Not Graded",
              maxScore,
              submissionDate: paper.submittedAt
                ? new Date(paper.submittedAt).toLocaleDateString()
                : "Not Submitted",
              status: paper.score !== null ? "Completed" : "Pending",
              // Add referenceId for download function
              referenceId: referenceId,
            });
          }
        });
      });
    });
    return data;
  }, [filteredData, periodDisplayMap]);

  const handleFilterChange = useCallback(
    (filterType, value) => {
      setFilters((prev) => {
        let next = { ...prev, [filterType]: value };

        if (filterType === "testType") {
          next.testId = "all";
        }

        if (
          filterType === "classId" &&
          value !== "all" &&
          prev.level === "all"
        ) {
          const cls = dashboardData?.classes?.find((c) => c._id === value);
          if (cls?.level) {
            next.level = cls.level;
          }
        }

        return next;
      });
    },
    [dashboardData]
  );

  const handleTableFilterChange = useCallback(
    (columnKey, value) => {
      const filterMapping = {
        period: "period",
        level: "level",
        testType: "testType",
        className: "classId",
        classLevel: "level",
        instructor: "instructorId",
        testName: "testId",
        studentName: "student",
        studentId: "student",
      };
      const filterKey = filterMapping[columnKey];
      if (!filterKey) return;

      if (columnKey === "studentId" && value !== "all") {
        return handleFilterChange("student", value);
      }
      if (columnKey === "studentName" && value !== "all") {
        const student = (students || []).find(
          (s) => `${s.firstName} ${s.lastName}` === value
        );
        if (student) handleFilterChange("student", student._id);
        return;
      }
      if (columnKey === "period" && value !== "all") {
        const periodEntry = Object.entries(periodDisplayMap).find(
          ([, display]) => display === value
        );
        if (periodEntry) handleFilterChange("period", periodEntry[0]);
        return;
      }
      if (columnKey === "className" && value !== "all") {
        const classEntry = (filterOptions.classNames || []).find(
          (c) => c.name === value
        );
        if (classEntry) handleFilterChange("classId", classEntry.id);
        return;
      }
      if (columnKey === "instructor" && value !== "all") {
        const inst = (filterOptions.instructors || []).find(
          (i) => i.name === value
        );
        if (inst) handleFilterChange("instructorId", inst.id);
        return;
      }
      if (columnKey === "testName" && value !== "all") {
        const testEntry = (filterOptions.testNames || []).find(
          (t) => t.name === value
        );
        if (testEntry) handleFilterChange("testId", testEntry.id);
        return;
      }
      if (columnKey === "testType" && value !== "all") {
        handleFilterChange("testType", value);
        return;
      }
      handleFilterChange(filterKey, value || "all");
    },
    [students, periodDisplayMap, handleFilterChange, filterOptions]
  );

  const clearAllFilters = () => {
    setFilters((prev) => ({
      period: prev.period,
      level: "all",
      testType: "all",
      classId: "all",
      instructorId: "all",
      testId: "all",
      student: "all",
    }));
  };

  const waitForResources = (container, timeout = 1200) =>
    new Promise((resolve) => {
      const imgs = Array.from(container.querySelectorAll("img"));
      if (imgs.length === 0) return setTimeout(resolve, 50);
      let loaded = 0;
      const check = () => {
        loaded++;
        if (loaded >= imgs.length) resolve();
      };
      imgs.forEach((img) => {
        if (img.complete) check();
        else {
          img.addEventListener("load", check, { once: true });
          img.addEventListener("error", check, { once: true });
        }
      });
      setTimeout(resolve, timeout);
    });

  const renderComponentToCanvas = async (
    Component,
    props = {},
    options = {}
  ) => {
    const [{ default: html2canvas }] = await Promise.all([
      import("html2canvas"),
    ]);

    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "-9999px";
    container.style.top = "0";
    container.style.width = options.width ? `${options.width}px` : "1200px";
    container.style.background = "#fff";
    document.body.appendChild(container);

    const root = createRoot(container);

    const PdfWrapper = () => {
      return (
        <div style={{ padding: 12 }}>
          <Component {...props} instituteInfo={instituteInfo} />
        </div>
      );
    };

    root.render(<PdfWrapper />);

    await waitForResources(container, 1200);
    await new Promise((r) => setTimeout(r, 250));

    const canvas = await html2canvas(container, {
      scale: options.scale || 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      windowWidth: container.offsetWidth,
      windowHeight: container.offsetHeight,
    });

    try {
      root.unmount();
    } catch {}
    container.remove();

    return canvas;
  };

  const addCanvasPagesToDoc = (
    doc,
    canvas,
    xMargin,
    yMargin,
    usableWidthMm,
    usableHeightMm,
    isFirstCanvas
  ) => {
    const imgWidthPx = canvas.width;
    const imgHeightPx = canvas.height;
    const pxPerMm = imgWidthPx / usableWidthMm;
    const sliceHeightPx = Math.floor(usableHeightMm * pxPerMm);

    if (imgHeightPx <= sliceHeightPx) {
      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const renderHeightMm = imgHeightPx / pxPerMm;
      const y = yMargin + (usableHeightMm - renderHeightMm) / 2;

      const currentPages = doc.internal.getNumberOfPages();
      if (isFirstCanvas && currentPages === 1) {
        doc.addImage(
          imgData,
          "JPEG",
          xMargin,
          y,
          usableWidthMm,
          renderHeightMm,
          undefined,
          "FAST"
        );
      } else {
        doc.addPage();
        doc.addImage(
          imgData,
          "JPEG",
          xMargin,
          yMargin,
          usableWidthMm,
          renderHeightMm,
          undefined,
          "FAST"
        );
      }
    } else {
      let yOffsetPx = 0;
      let sliceIndex = 0;
      while (yOffsetPx < imgHeightPx) {
        const sliceH = Math.min(sliceHeightPx, imgHeightPx - yOffsetPx);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = imgWidthPx;
        sliceCanvas.height = sliceH;
        const ctx = sliceCanvas.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(
          canvas,
          0,
          yOffsetPx,
          imgWidthPx,
          sliceH,
          0,
          0,
          imgWidthPx,
          sliceH
        );

        const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.95);
        const renderHeightMm = sliceH / pxPerMm;

        if (
          isFirstCanvas &&
          sliceIndex === 0 &&
          doc.internal.getNumberOfPages() === 1 &&
          doc.getCurrentPageInfo().pageNumber === 1
        ) {
          doc.addImage(
            sliceData,
            "JPEG",
            xMargin,
            yMargin,
            usableWidthMm,
            renderHeightMm,
            undefined,
            "FAST"
          );
        } else {
          doc.addPage();
          doc.addImage(
            sliceData,
            "JPEG",
            xMargin,
            yMargin,
            usableWidthMm,
            renderHeightMm,
            undefined,
            "FAST"
          );
        }

        yOffsetPx += sliceH;
        sliceIndex++;
      }
    }
  };

  const generateCombinedPdf = async (
    reportComp,
    reportProps = {},
    perfComp = null,
    perfProps = {},
    options = {}
  ) => {
    const [{ jsPDF }] = await Promise.all([import("jspdf")]);

    const reportCanvas = await renderComponentToCanvas(
      reportComp,
      reportProps,
      options
    );
    let perfCanvas = null;
    if (perfComp) {
      perfCanvas = await renderComponentToCanvas(perfComp, perfProps, options);
    }

    const A4_WIDTH_MM = 297;
    const A4_HEIGHT_MM = 210;
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: [A4_WIDTH_MM, A4_HEIGHT_MM],
    });

    const pdfWidth = doc.internal.pageSize.getWidth();
    const pdfHeight = doc.internal.pageSize.getHeight();

    const xMargin = 5;
    const yMargin = 5;
    const usableWidthMm = pdfWidth - xMargin * 2;
    const usableHeightMm = pdfHeight - yMargin * 2;

    addCanvasPagesToDoc(
      doc,
      reportCanvas,
      xMargin,
      yMargin,
      usableWidthMm,
      usableHeightMm,
      true
    );

    if (perfCanvas) {
      const addSinglePageCanvasToDoc = (
        doc,
        canvas,
        xMargin,
        yMargin,
        useExistingFirstPage = false
      ) => {
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();
        const usableW = pageW - xMargin * 2;
        const usableH = pageH - yMargin * 2;

        const imgWidthPx = canvas.width;
        const imgHeightPx = canvas.height;
        const imgAspect = imgWidthPx / imgHeightPx;

        let renderW = usableW;
        let renderH = renderW / imgAspect;
        if (renderH > usableH) {
          renderH = usableH;
          renderW = renderH * imgAspect;
        }

        const x = xMargin + (usableW - renderW) / 2;
        const y = yMargin + (usableH - renderH) / 2;
        const imgData = canvas.toDataURL("image/jpeg", 0.95);

        const currentPages = doc.internal.getNumberOfPages();
        const onFirstPage =
          currentPages === 1 && doc.getCurrentPageInfo().pageNumber === 1;

        if (useExistingFirstPage && onFirstPage) {
          doc.addImage(
            imgData,
            "JPEG",
            x,
            y,
            renderW,
            renderH,
            undefined,
            "FAST"
          );
        } else {
          doc.addPage();
          doc.addImage(
            imgData,
            "JPEG",
            x,
            y,
            renderW,
            renderH,
            undefined,
            "FAST"
          );
        }
      };

      addSinglePageCanvasToDoc(doc, perfCanvas, xMargin, yMargin, false);
    }

    return doc.output("blob");
  };

  const mergePdfs = async (frontBlob, backBlob) => {
    try {
      const { PDFDocument } = await import("pdf-lib");
      const frontBytes = frontBlob ? await frontBlob.arrayBuffer() : null;
      const backBytes = backBlob ? await backBlob.arrayBuffer() : null;

      const mergedPdf = await PDFDocument.create();

      if (frontBytes && frontBytes.byteLength) {
        const frontDoc = await PDFDocument.load(frontBytes);
        const frontPages = await mergedPdf.copyPages(
          frontDoc,
          frontDoc.getPageIndices()
        );
        frontPages.forEach((p) => mergedPdf.addPage(p));
      }

      if (backBytes && backBytes.byteLength) {
        const backDoc = await PDFDocument.load(backBytes);
        const backPages = await mergedPdf.copyPages(
          backDoc,
          backDoc.getPageIndices()
        );
        backPages.forEach((p) => mergedPdf.addPage(p));
      }

      const mergedBytes = await mergedPdf.save();
      return new Blob([mergedBytes], { type: "application/pdf" });
    } catch (err) {
      console.warn("mergePdfs failed:", err);
      return backBlob;
    }
  };

  const handleDownloadAllWithPrompt = async () => {
    try {
      const choice = await swalConfirm(
        "Include Answer Paper?",
        "Choose whether to include the original answer-paper PDF with each student's report.",
        "With Answer Paper",
        "Without Answer Paper",
        true
      );

      if (!choice) {
        return;
      }

      let includeAnswerPaper = false;
      if (choice.isConfirmed) {
        includeAnswerPaper = true;
      } else if (choice.dismiss === "cancel") {
        includeAnswerPaper = false;
      } else {
        return;
      }

      // Set the state
      setDownloadWithAnswerPaper(includeAnswerPaper);

      // Pass the value directly instead of relying on state
      await handleDownloadAllReports(includeAnswerPaper);
    } catch (err) {
      console.error("handleDownloadAllWithPrompt error", err);
      swalError("Failed to start download.");
    }
  };

  const handleDownloadAllReports = async (
    includeAnswerPaper = downloadWithAnswerPaper
  ) => {
    setIsDownloading(true);
    setDownloadPhase("preparing");
    setDownloadProgress("Preparing download process...");

    try {
      if (gradedStudentsForTest.length === 0) {
        swalError("No graded submissions to download.");
        setIsDownloading(false);
        return;
      }

      setDownloadPhase("generating");
      setDownloadProgress("Generating PDF reports...");

      for (const student of gradedStudentsForTest) {
        setCurrentStudentName(`${student.firstName} ${student.lastName}`);
        setDownloadPhase("downloading");
        setDownloadProgress(
          `Preparing download for ${student.firstName} ${student.lastName}...`
        );

        try {
          // Fetch detailed answer paper
          const resp = await fetchWithAuth(
            `${serverUrl}/api/answer-paper/get/${student._id}`,
            { method: "GET", credentials: "include" }
          );

          let ap = {};
          if (resp && resp.ok) {
            const respJson = await resp.json();
            ap = respJson?.data || respJson || {};
          }

          // Fetch detailed score
          let resultData = [];
          let summary = {
            overallPerformance: "",
            strengths: [],
            weaknesses: [],
            recommendations: [],
          };
          try {
            const scoreResp = await fetchWithAuth(
              `${serverUrl}/api/score/get/${student._id}`,
              { credentials: "include" }
            );
            if (scoreResp && scoreResp.ok) {
              const scoreJson = await scoreResp.json();
              if (scoreJson.score && Array.isArray(scoreJson.score.result)) {
                resultData = scoreJson.score.result;
                summary = scoreJson.score.summary || {};
              }
            }
          } catch (err) {
            console.warn("Failed to fetch score details for", student._id, err);
          }

          const studentName =
            `${student.firstName} ${student.lastName}`.trim() ||
            student.referenceId;
          const referenceId = student.referenceId || "";

          const summaryPresent =
            ap.summary ||
            ap.summaryStatus === "Summarized" ||
            student.summaryStatus === "Summarized";

          const reportProps = {
            testName: student.testName || "",
            studentId: referenceId,
            studentName,
            resultData,
            instituteInfo,
          };

          let perfProps = null;
          if (summaryPresent) {
            const summaryObj = summary || {};
            perfProps = {
              summary: {
                studentName,
                testName: student.testName || "",
                instructorName: "",
                overallPerformance: summaryObj?.overallPerformance || "",
                strengths: summaryObj?.strengths || [],
                weaknesses: summaryObj?.weaknesses || [],
                recommendations: summaryObj?.recommendations || [],
                createdAt: summaryObj?.createdAt || "",
                updatedAt: summaryObj?.updatedAt || "",
              },
              isSummaryEditing: false,
              onSaveSummary: false,
              handleSaveSummary: () => {},
              instituteInfo,
              classInfo: {
                name: student.className || "",
                level: student.classLevel || "",
              },
            };
          }

          const combinedBlob = await generateCombinedPdf(
            Report,
            reportProps,
            summaryPresent ? PerformanceReport : null,
            perfProps,
            { width: 1200, scale: 2 }
          );

          let finalBlob = combinedBlob;
          // Use the parameter instead of state - this was the main issue
          if (includeAnswerPaper && ap && ap.url) {
            try {
              const pdfResp = await fetch(ap.url);
              if (pdfResp && pdfResp.ok) {
                const answerBlob = await pdfResp.blob();
                finalBlob = await mergePdfs(answerBlob, combinedBlob);
              }
            } catch (err) {
              swalError(`Failed to include answer paper for ${studentName}.`);
            }
          }

          const safeStudentName = studentName
            .replace(/[/\\?%*:|"<>]/g, "_")
            .trim();
          const safeRef = String(referenceId).replace(/[/\\?%*:|"<>]/g, "_");
          const filename = `Score Report-${safeStudentName}(${safeRef}).pdf`;

          const pdfUrl = URL.createObjectURL(finalBlob);
          const link = document.createElement("a");
          link.href = pdfUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          setTimeout(() => URL.revokeObjectURL(pdfUrl), 5000);
        } catch (err) {
          console.error("Failed to process student", student._id, err);
        }
      }

      setDownloadPhase("completed");
      setDownloadProgress("All reports downloaded successfully!");
      setCurrentStudentName("");

      swalSuccess("All reports downloaded successfully.");
      setIsDownloadModalOpen(false);
    } catch (err) {
      swalError("Failed to generate reports for download.");
    } finally {
      setIsDownloading(false);
      setDownloadPhase("");
      setDownloadProgress("");
      setCurrentStudentName("");
    }
  };

  const handleDownloadSingle = async (studentData) => {
    setActiveAction({ id: studentData._id, type: "download" });
    try {
      // Ask user whether to include the original answer-paper
      const choice = await swalConfirm(
        "Include Answer Paper?",
        "Choose whether to include the original answer-paper PDF with the report.",
        "With Answer Paper",
        "Without Answer Paper",
        true
      );

      if (!choice) {
        return; // No response, abort
      }

      let includeAnswerPaper = false;
      if (choice.isConfirmed) {
        includeAnswerPaper = true;
      } else if (choice.dismiss === "cancel") {
        includeAnswerPaper = false;
      } else {
        return; // User dismissed, abort
      }

      // Fetch detailed answer paper
      const resp = await fetchWithAuth(
        `${serverUrl}/api/answer-paper/get/${studentData._id}`,
        { method: "GET", credentials: "include" }
      );

      let ap = {};
      if (resp && resp.ok) {
        const respJson = await resp.json();
        ap = respJson?.data || respJson || {};
      }

      // Fetch detailed score
      let resultData = [];
      let summary = {
        overallPerformance: "",
        strengths: [],
        weaknesses: [],
        recommendations: [],
      };
      try {
        const scoreResp = await fetchWithAuth(
          `${serverUrl}/api/score/get/${studentData._id}`,
          { credentials: "include" }
        );
        if (scoreResp && scoreResp.ok) {
          const scoreJson = await scoreResp.json();
          if (scoreJson.score && Array.isArray(scoreJson.score.result)) {
            resultData = scoreJson.score.result;
            summary = scoreJson.score.summary || {
              overallPerformance: "",
              strengths: [],
              weaknesses: [],
              recommendations: [],
            };
          }
        }
      } catch (err) {
        console.warn("Failed to fetch score details for", studentData._id, err);
      }

      const studentName =
        `${studentData.firstName} ${studentData.lastName}`.trim() ||
        studentData.referenceId;
      const referenceId = studentData.referenceId || "";

      const summaryPresent =
        ap.summary ||
        ap.summaryStatus === "Summarized" ||
        studentData.summaryStatus === "Summarized";

      const reportProps = {
        testName: studentData.testName || "",
        studentId: referenceId,
        studentName,
        resultData,
        instituteInfo,
      };

      let perfProps = null;
      if (summaryPresent) {
        const summaryObj = summary || {};
        perfProps = {
          summary: {
            studentName,
            testName: studentData.testName || "",
            instructorName: "",
            overallPerformance: summaryObj?.overallPerformance || "",
            strengths: summaryObj?.strengths || [],
            weaknesses: summaryObj?.weaknesses || [],
            recommendations: summaryObj?.recommendations || [],
            createdAt: summaryObj?.createdAt || "",
            updatedAt: summaryObj?.updatedAt || "",
          },
          isSummaryEditing: false,
          onSaveSummary: false,
          handleSaveSummary: () => {},
          instituteInfo,
          classInfo: {
            name: studentData.className || "",
            level: studentData.classLevel || "",
          },
        };
      }

      const combinedBlob = await generateCombinedPdf(
        Report,
        reportProps,
        summaryPresent ? PerformanceReport : null,
        perfProps,
        { width: 1200, scale: 2 }
      );

      // Merge answer paper if requested and available
      let finalBlob = combinedBlob;
      if (includeAnswerPaper && ap && ap.url) {
        try {
          const pdfResp = await fetch(ap.url);
          if (pdfResp && pdfResp.ok) {
            const answerBlob = await pdfResp.blob();
            finalBlob = await mergePdfs(answerBlob, combinedBlob);
          }
        } catch (err) {
          console.warn(
            "Failed to fetch/merge answer paper for",
            studentData._id,
            err
          );
        }
      }

      const safeStudentName = studentName.replace(/[/\\?%*:|"<>]/g, "_").trim();
      const safeRef = String(referenceId).replace(/[/\\?%*:|"<>]/g, "_");
      const filename = `Score Report-${safeStudentName}(${safeRef}).pdf`;

      const pdfUrl = URL.createObjectURL(finalBlob);
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 5000);

      swalSuccess("Report downloaded successfully!");
    } catch (err) {
      console.error("handleDownloadSingle error", err);
      swalError("Failed to prepare download.");
    } finally {
      setActiveAction({ id: null, type: null });
    }
  };

  const renderKPICards = () => {
    if (!analytics) return null;
    const { kpiData, scenario } = analytics;

    if (scenario?.isStudentTestType) {
      return (
        <>
          <div className="kpi-card primary">
            <div className="kpi-icon">👤</div>
            <div className="kpi-content">
              <h3>Student</h3>
              <p className="kpi-number">{kpiData.studentName}</p>
            </div>
          </div>
          <div className="kpi-card success">
            <div className="kpi-icon">🏷️</div>
            <div className="kpi-content">
              <h3>Test Type</h3>
              <p className="kpi-number">{kpiData.testType}</p>
            </div>
          </div>
          <div className="kpi-card warning">
            <div className="kpi-icon">🏆</div>
            <div className="kpi-content">
              <h3>Overall %</h3>
              <p className="kpi-number">
                {formatNumber(kpiData.overallPercentage)}%
              </p>
            </div>
          </div>
          <div className="kpi-card info">
            <div className="kpi-icon">📚</div>
            <div className="kpi-content">
              <h3>Subjects</h3>
              <p className="kpi-number">{kpiData.totalSubjects}</p>
            </div>
          </div>
          <div className="kpi-card purple">
            <div className="kpi-icon">📝</div>
            <div className="kpi-content">
              <h3>Tests</h3>
              <p className="kpi-number">{kpiData.totalTests}</p>
            </div>
          </div>
        </>
      );
    }

    if (scenario?.isTestTypeOnly) {
      return (
        <>
          <div className="kpi-card primary">
            <div className="kpi-icon">🏷️</div>
            <div className="kpi-content">
              <h3>Test Type</h3>
              <p className="kpi-number">{kpiData.testType}</p>
            </div>
          </div>
          <div className="kpi-card success">
            <div className="kpi-icon">📚</div>
            <div className="kpi-content">
              <h3>Classes</h3>
              <p className="kpi-number">{kpiData.totalClasses}</p>
            </div>
          </div>
          <div className="kpi-card info">
            <div className="kpi-icon">👥</div>
            <div className="kpi-content">
              <h3>Students</h3>
              <p className="kpi-number">{kpiData.totalStudents}</p>
            </div>
          </div>
          <div className="kpi-card purple">
            <div className="kpi-icon">🏆</div>
            <div className="kpi-content">
              <h3>Overall Avg</h3>
              <p className="kpi-number">
                {formatNumber(kpiData.overallAverage)}%
              </p>
            </div>
          </div>
        </>
      );
    }

    if (scenario?.isTestTypeAndClass) {
      return (
        <>
          <div className="kpi-card primary">
            <div className="kpi-icon">📚</div>
            <div className="kpi-content">
              <h3>Class</h3>
              <p className="kpi-number">{kpiData.className}</p>
            </div>
          </div>
          <div className="kpi-card success">
            <div className="kpi-icon">🏷️</div>
            <div className="kpi-content">
              <h3>Test Type</h3>
              <p className="kpi-number">{kpiData.testType}</p>
            </div>
          </div>
          <div className="kpi-card warning">
            <div className="kpi-icon">👥</div>
            <div className="kpi-content">
              <h3>Students</h3>
              <p className="kpi-number">{kpiData.totalStudents}</p>
            </div>
          </div>
          <div className="kpi-card info">
            <div className="kpi-icon">📄</div>
            <div className="kpi-content">
              <h3>Tests</h3>
              <p className="kpi-number">{kpiData.totalTests}</p>
            </div>
          </div>
          <div className="kpi-card purple">
            <div className="kpi-icon">🏆</div>
            <div className="kpi-content">
              <h3>Class Avg</h3>
              <p className="kpi-number">
                {formatNumber(kpiData.classAverage)}%
              </p>
            </div>
          </div>
        </>
      );
    }

    if (scenario?.isStudent) {
      return (
        <>
          <div className="kpi-card primary">
            <div className="kpi-icon">👤</div>
            <div className="kpi-content">
              <h3>Student</h3>
              <p className="kpi-number">{kpiData.studentName}</p>
            </div>
          </div>
          <div className="kpi-card success">
            <div className="kpi-icon">🧾</div>
            <div className="kpi-content">
              <h3>Total Score</h3>
              <p className="kpi-number">
                {formatNumber(kpiData.totalScoreSum)} /{" "}
                {formatNumber(kpiData.totalMaxSum)}
              </p>
            </div>
          </div>
          <div className="kpi-card warning">
            <div className="kpi-icon">🏆</div>
            <div className="kpi-content">
              <h3>Overall %</h3>
              <p className="kpi-number">
                {formatNumber(kpiData.overallPercentage)}%
              </p>
            </div>
          </div>
          <div className="kpi-card info">
            <div className="kpi-icon">📚</div>
            <div className="kpi-content">
              <h3>Subjects</h3>
              <p className="kpi-number">{kpiData.totalSubjects}</p>
            </div>
          </div>
          <div className="kpi-card purple">
            <div className="kpi-icon">📝</div>
            <div className="kpi-content">
              <h3>Tests</h3>
              <p className="kpi-number">{kpiData.totalTests}</p>
            </div>
          </div>
        </>
      );
    }

    if (scenario.isOnlyPeriod) {
      return (
        <>
          <div className="kpi-card primary">
            <div className="kpi-icon">👥</div>
            <div className="kpi-content">
              <h3>Total Students</h3>
              <p className="kpi-number">{kpiData.totalStudents}</p>
            </div>
          </div>
          <div className="kpi-card success">
            <div className="kpi-icon">👨‍🏫</div>
            <div className="kpi-content">
              <h3>Total Instructors</h3>
              <p className="kpi-number">{kpiData.totalInstructors}</p>
            </div>
          </div>
          <div className="kpi-card warning">
            <div className="kpi-icon">📚</div>
            <div className="kpi-content">
              <h3>Total Subjects</h3>
              <p className="kpi-number">{kpiData.totalSubjects}</p>
            </div>
          </div>
          <div className="kpi-card info">
            <div className="kpi-icon">🎓</div>
            <div className="kpi-content">
              <h3>Total Levels</h3>
              <p className="kpi-number">{kpiData.totalLevels}</p>
            </div>
          </div>
        </>
      );
    }

    if (scenario.isPeriodAndLevel) {
      return (
        <>
          <div className="kpi-card primary">
            <div className="kpi-icon">👥</div>
            <div className="kpi-content">
              <h3>Total Students</h3>
              <p className="kpi-number">{kpiData.totalStudents}</p>
            </div>
          </div>
          <div className="kpi-card success">
            <div className="kpi-icon">👨‍🏫</div>
            <div className="kpi-content">
              <h3>Total Instructors</h3>
              <p className="kpi-number">{kpiData.totalInstructors}</p>
            </div>
          </div>
          <div className="kpi-card warning">
            <div className="kpi-icon">📚</div>
            <div className="kpi-content">
              <h3>Total Subjects</h3>
              <p className="kpi-number">{kpiData.totalSubjects}</p>
            </div>
          </div>
          <div className="kpi-card info">
            <div className="kpi-icon">📝</div>
            <div className="kpi-content">
              <h3>Total Tests</h3>
              <p className="kpi-number">{kpiData.totalTests}</p>
            </div>
          </div>
          <div className="kpi-card purple">
            <div className="kpi-icon">🏆</div>
            <div className="kpi-content">
              <h3>Level Average ({kpiData.levelName})</h3>
              <p className="kpi-number">
                {formatNumber(kpiData.overallAverage)}%
              </p>
            </div>
          </div>
        </>
      );
    }

    if (scenario.isPeriodLevelAndSubject) {
      return (
        <>
          <div className="kpi-card primary">
            <div className="kpi-icon">👥</div>
            <div className="kpi-content">
              <h3>Total Students</h3>
              <p className="kpi-number">{kpiData.totalStudents}</p>
            </div>
          </div>
          <div className="kpi-card success">
            <div className="kpi-icon">📝</div>
            <div className="kpi-content">
              <h3>Total Tests</h3>
              <p className="kpi-number">{kpiData.totalTests}</p>
            </div>
          </div>
          <div className="kpi-card warning">
            <div className="kpi-icon">🏆</div>
            <div className="kpi-content">
              <h3>{kpiData.subjectName} Average</h3>
              <p className="kpi-number">
                {formatNumber(kpiData.subjectAverage)}%
              </p>
            </div>
          </div>
          <div className="kpi-card info">
            <div className="kpi-icon">🎓</div>
            <div className="kpi-content">
              <h3>Level</h3>
              <p className="kpi-number">{kpiData.levelName}</p>
            </div>
          </div>
        </>
      );
    }

    if (scenario.isPeriodLevelClassAndTest) {
      return (
        <>
          <div className="kpi-card primary">
            <div className="kpi-icon">👥</div>
            <div className="kpi-content">
              <h3>Total Students</h3>
              <p className="kpi-number">{kpiData.totalStudents}</p>
            </div>
          </div>
          <div className="kpi-card success">
            <div className="kpi-icon">📝</div>
            <div className="kpi-content">
              <h3>Test</h3>
              <p className="kpi-number">{kpiData.testName}</p>
            </div>
          </div>
          <div className="kpi-card warning">
            <div className="kpi-icon">🏆</div>
            <div className="kpi-content">
              <h3>Test Average</h3>
              <p className="kpi-number">{formatNumber(kpiData.testAverage)}%</p>
            </div>
          </div>
          <div className="kpi-card info">
            <div className="kpi-icon">📚</div>
            <div className="kpi-content">
              <h3>Subject</h3>
              <p className="kpi-number">{kpiData.subjectName}</p>
            </div>
          </div>
        </>
      );
    }

    if (scenario?.isStudentRole) {
      return (
        <>
          <div className="kpi-card primary">
            <div className="kpi-icon">👤</div>
            <div className="kpi-content">
              <h3>Student</h3>
              <p className="kpi-number">{kpiData.studentName || "You"}</p>
            </div>
          </div>
          <div className="kpi-card success">
            <div className="kpi-icon">🧾</div>
            <div className="kpi-content">
              <h3>Total Score</h3>
              <p className="kpi-number">
                {formatNumber(kpiData.totalScoreSum)} /{" "}
                {formatNumber(kpiData.totalMaxSum)}
              </p>
            </div>
          </div>
          <div className="kpi-card warning">
            <div className="kpi-icon">🏆</div>
            <div className="kpi-content">
              <h3>Overall %</h3>
              <p className="kpi-number">
                {formatNumber(kpiData.overallPercentage)}%
              </p>
            </div>
          </div>
          <div className="kpi-card info">
            <div className="kpi-icon">📚</div>
            <div className="kpi-content">
              <h3>Subjects</h3>
              <p className="kpi-number">{kpiData.totalSubjects}</p>
            </div>
          </div>
          <div className="kpi-card purple">
            <div className="kpi-icon">📝</div>
            <div className="kpi-content">
              <h3>Tests</h3>
              <p className="kpi-number">{kpiData.totalTests}</p>
            </div>
          </div>
        </>
      );
    }

    return null;
  };

  const renderCharts = () => {
    if (!analytics) return null;
    const { chartData, scenario } = analytics;

    const commonBarOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "top" },
        tooltip: {
          backgroundColor: "rgba(0,0,0,0.9)",
          titleColor: "#fff",
          bodyColor: "#fff",
          callbacks: {
            label: (ctx) =>
              `${ctx.dataset.label}: ${formatNumber(
                ctx.parsed?.y ?? ctx.parsed
              )}%`,
            afterLabel: () => {
              return ["", "💡 Click to apply filter"];
            },
          },
        },
      },
      scales: {
        x: { grid: { display: false } },
        y: {
          grid: { color: "rgba(0,0,0,0.05)" },
          ticks: { callback: (v) => `${v}%` },
        },
      },
      onHover: (event, activeElements) => {
        event.native.target.style.cursor =
          activeElements.length > 0 ? "pointer" : "default";
      },
    };

    // Helper function to create click handler for different chart types
    const createChartClickHandler = (filterType) => (event, activeElements) => {
      if (activeElements.length > 0) {
        const clickedIndex = activeElements[0].index;
        let filterValue = null;

        switch (filterType) {
          case "level":
            filterValue = (chartData.avgByLevel || [])[clickedIndex]?.level;
            if (filterValue) handleFilterChange("level", filterValue);
            break;
          case "subject":
            // For subject analysis, use the stored classId directly
            const subjectData = (chartData.avgBySubject || [])[clickedIndex];
            if (subjectData && subjectData.classId) {
              // Use the stored classId directly
              handleFilterChange("classId", subjectData.classId);
            } else if (
              subjectData &&
              subjectData.subject &&
              dashboardData?.classes
            ) {
              // Fallback to name matching if classId not available
              const foundClass = dashboardData.classes.find(
                (cls) => cls.name === subjectData.subject
              );
              if (foundClass) {
                handleFilterChange("classId", foundClass._id);
              }
            }
            break;
          case "test":
            // For test analysis, use the stored test ID if available, otherwise find by name
            const testData = (chartData.avgByTest || [])[clickedIndex];
            if (testData) {
              if (testData.testId) {
                handleFilterChange("testId", testData.testId);
              } else if (testData.testName && dashboardData?.classes) {
                const foundTest = dashboardData.classes
                  .flatMap((cls) => cls.tests || [])
                  .find((test) => test.testName === testData.testName);
                if (foundTest) {
                  handleFilterChange("testId", foundTest._id);
                }
              }
            }
            break;
          case "student":
            // For student analysis, use stored student ID if available
            const studentData = Object.entries(chartData.scoresByStudent || {})[
              clickedIndex
            ];
            if (studentData) {
              const [studentName] = studentData;
              // Try to find by stored ID first, then by name matching
              const foundStudent = students.find(
                (s) =>
                  `${s.firstName} ${s.lastName}`.trim() === studentName.trim()
              );
              if (foundStudent) {
                handleFilterChange("student", foundStudent._id);
              }
            }
            break;
          case "class":
            // For class analysis, use stored class ID if available
            const classData = (chartData.avgByClass || [])[clickedIndex];
            if (classData) {
              if (classData.classId) {
                handleFilterChange("classId", classData.classId);
              } else if (classData.className && dashboardData?.classes) {
                const foundClass = dashboardData.classes.find(
                  (cls) => cls.name === classData.className
                );
                if (foundClass) {
                  handleFilterChange("classId", foundClass._id);
                }
              }
            }
            break;
          case "studentInClass":
            // For student averages in test type and class scenario
            const studentInClassData = (chartData.studentAverages || [])[
              clickedIndex
            ];
            if (studentInClassData) {
              if (studentInClassData.studentId) {
                handleFilterChange("student", studentInClassData.studentId);
              } else if (studentInClassData.student) {
                const foundStudent = students.find(
                  (s) =>
                    `${s.firstName} ${s.lastName}` ===
                    studentInClassData.student
                );
                if (foundStudent) {
                  handleFilterChange("student", foundStudent._id);
                }
              }
            }
            break;
          case "studentTestType":
            // For student in testType scenario
            const studentTestTypeData = (chartData.avgByStudent || [])[
              clickedIndex
            ];
            if (studentTestTypeData) {
              if (studentTestTypeData.studentId) {
                handleFilterChange("student", studentTestTypeData.studentId);
              } else if (studentTestTypeData.student) {
                const foundStudent = students.find(
                  (s) =>
                    `${s.firstName} ${s.lastName}` ===
                    studentTestTypeData.student
                );
                if (foundStudent) {
                  handleFilterChange("student", foundStudent._id);
                }
              }
            }
            break;
          default:
            break;
        }
      }
    };

    // Add custom options for question stats chart
    const questionStatsOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: {
            generateLabels: () => [
              {
                text: "Performance by Question",
                fillStyle: "rgba(99, 102, 241, 0.8)",
                strokeStyle: "rgba(99, 102, 241, 1)",
                lineWidth: 2,
              },
            ],
          },
        },
        tooltip: {
          backgroundColor: "rgba(0,0,0,0.9)",
          titleColor: "#fff",
          bodyColor: "#fff",
          titleFont: { size: 14, weight: "bold" },
          bodyFont: { size: 12 },
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            title: (tooltipItems) => {
              const index = tooltipItems[0].dataIndex;
              const stats = questionStats[index];
              return `Question ${stats.questionNumber}`;
            },
            label: (ctx) => {
              const index = ctx.dataIndex;
              const stats = questionStats[index];
              return [
                `Overall Performance: ${formatNumber(ctx.parsed.y)}%`,
                `Students Attempted: ${stats.studentCount}`,
                `Max Score: ${formatNumber(
                  stats.totalMax / stats.studentCount
                )}`,
                `Average Score: ${formatNumber(stats.average)}`,
                `Median Score: ${formatNumber(stats.median)}`,
                "",
                `Question: ${stats.questionText}`,
              ];
            },
            afterLabel: () => {
              return ["", "💡 Click to view detailed question analysis"];
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          title: {
            display: true,
            text: "Question Number",
            font: { weight: "bold" },
          },
        },
        y: {
          grid: { color: "rgba(0,0,0,0.05)" },
          ticks: {
            callback: (v) => `${v}%`,
            font: { weight: "500" },
          },
          title: {
            display: true,
            text: "Performance (%)",
            font: { weight: "bold" },
          },
          min: 0,
          max: 100,
        },
      },
    };

    if (chartData.type === "studentTestTypeMulti") {
      const subjects = Object.keys(chartData.subjectTests || {});
      return (
        <div className="charts-grid">
          <div className="chart-card large">
            <div className="chart-header">
              <h3>📊 Student ({filters.testType}) - Avg by Subject</h3>
              <div className="chart-value">Subject Performance</div>
            </div>
            <div className="chart-container">
              <Bar
                data={{
                  labels: (chartData.avgBySubject || []).map((i) => i.subject),
                  datasets: [
                    {
                      label: "Average",
                      data: (chartData.avgBySubject || []).map(
                        (i) => i.avgPercentage
                      ),
                      backgroundColor: "rgba(52,211,153,0.8)",
                      borderColor: "rgba(52,211,153,1)",
                      borderWidth: 2,
                      borderRadius: 8,
                    },
                  ],
                }}
                options={{
                  ...commonBarOptions,
                  onClick: createChartClickHandler("subject"),
                }}
              />
            </div>
          </div>
          {subjects.map((subject) => {
            const tests = chartData.subjectTests[subject] || [];
            return (
              <div className="chart-card large" key={subject}>
                <div className="chart-header">
                  <h3>
                    📝 {subject} ({filters.testType}) Tests
                  </h3>
                  <div className="chart-value">
                    {tests.length} {tests.length === 1 ? "Test" : "Tests"}
                  </div>
                </div>
                <div className="chart-container small">
                  <Bar
                    data={{
                      labels: tests.map((t) => t.testName),
                      datasets: [
                        {
                          label: "Percentage",
                          data: tests.map((t) => formatNumber(t.percentage)),
                          backgroundColor: "rgba(102,126,234,0.85)",
                          borderRadius: 8,
                        },
                      ],
                    }}
                    options={{
                      ...commonBarOptions,
                      onClick: (event, activeElements) => {
                        if (activeElements.length > 0) {
                          const clickedIndex = activeElements[0].index;
                          const testData = tests[clickedIndex];
                          if (testData) {
                            // Use testId if available, otherwise find by name
                            if (testData.testId) {
                              handleFilterChange("testId", testData.testId);
                            } else if (
                              testData.testName &&
                              dashboardData?.classes
                            ) {
                              const foundTest = dashboardData.classes
                                .flatMap((cls) => cls.tests || [])
                                .find(
                                  (test) => test.testName === testData.testName
                                );
                              if (foundTest) {
                                handleFilterChange("testId", foundTest._id);
                              }
                            }
                          }
                        }
                      },
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (chartData.type === "testTypeAllClasses") {
      return (
        <div className="charts-grid">
          <div className="chart-card large">
            <div className="chart-header">
              <h3>📚 Avg % by Class ({filters.testType})</h3>
              <div className="chart-value">Class Performance</div>
            </div>
            <div className="chart-container">
              <Bar
                data={{
                  labels: (chartData.avgByClass || []).map((i) => i.className),
                  datasets: [
                    {
                      label: "Average",
                      data: (chartData.avgByClass || []).map(
                        (i) => i.avgPercentage
                      ),
                      backgroundColor: "rgba(139,92,246,0.8)",
                      borderColor: "rgba(139,92,246,1)",
                      borderWidth: 2,
                      borderRadius: 8,
                    },
                  ],
                }}
                options={{
                  ...commonBarOptions,
                  onClick: createChartClickHandler("class"),
                }}
              />
            </div>
          </div>
          <div className="chart-card large">
            <div className="chart-header">
              <h3>👥 Student Avg % ({filters.testType})</h3>
              <div className="chart-value">
                {(chartData.avgByStudent || []).length} Students
              </div>
            </div>
            <div className="chart-container">
              <Bar
                data={{
                  labels: (chartData.avgByStudent || []).map((i) => i.student),
                  datasets: [
                    {
                      label: "Average",
                      data: (chartData.avgByStudent || []).map(
                        (i) => i.avgPercentage
                      ),
                      backgroundColor: "rgba(34,197,94,0.8)",
                      borderRadius: 8,
                    },
                  ],
                }}
                options={{
                  ...commonBarOptions,
                  onClick: createChartClickHandler("studentTestType"),
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    if (chartData.type === "testTypeClass") {
      return (
        <div className="charts-grid">
          <div className="chart-card fw">
            <div className="chart-header">
              <h3>👥 Student Avg % ({filters.testType}) - Class Performance</h3>
              <div className="chart-value">
                {(chartData.studentAverages || []).length} Students
              </div>
            </div>
            <div className="chart-container">
              <Bar
                data={{
                  labels: (chartData.studentAverages || []).map(
                    (i) => i.student
                  ),
                  datasets: [
                    {
                      label: "Average",
                      data: (chartData.studentAverages || []).map(
                        (i) => i.avgPercentage
                      ),
                      backgroundColor: (chartData.studentAverages || []).map(
                        (s) =>
                          s.avgPercentage >= 80
                            ? "rgba(34,197,94,0.8)"
                            : s.avgPercentage >= 60
                            ? "rgba(234,179,8,0.8)"
                            : "rgba(239,68,68,0.8)"
                      ),
                      borderRadius: 8,
                    },
                  ],
                }}
                options={{
                  ...commonBarOptions,
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    if (chartData.type === "studentSingleTest") {
      return (
        <div className="charts-grid">
          <div className="chart-card fw">
            <div className="chart-header">
              <h3>👥 Test Comparison - {chartData.specificTestName}</h3>
              <div className="chart-value">Student vs Classmates</div>
            </div>
            <div className="chart-container">
              <Bar
                data={{
                  labels: Object.keys(chartData.classmates || {}),
                  datasets: [
                    {
                      label: "Percentage",
                      data: Object.values(chartData.classmates || {}),
                      backgroundColor: Object.values(
                        chartData.classmates || {}
                      ).map((score) =>
                        score >= 80
                          ? "rgba(34,197,94,0.8)"
                          : score >= 60
                          ? "rgba(234,179,8,0.8)"
                          : "rgba(239,68,68,0.8)"
                      ),
                      borderRadius: 8,
                    },
                  ],
                }}
                options={{
                  ...commonBarOptions,
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    if (chartData.type === "studentMulti") {
      const subjects = Object.keys(chartData.subjectTests || {});
      return (
        <div className="charts-grid">
          <div className="chart-card large">
            <div className="chart-header">
              <h3>📊 Student - Average by Subject</h3>
              <div className="chart-value">Subject Performance Overview</div>
            </div>
            <div className="chart-container">
              <Bar
                data={{
                  labels: (chartData.avgBySubject || []).map((i) => i.subject),
                  datasets: [
                    {
                      label: "Average",
                      data: (chartData.avgBySubject || []).map(
                        (i) => i.avgPercentage
                      ),
                      backgroundColor: "rgba(52,211,153,0.8)",
                      borderColor: "rgba(52,211,153,1)",
                      borderWidth: 2,
                      borderRadius: 8,
                    },
                  ],
                }}
                options={{
                  ...commonBarOptions,
                }}
              />
            </div>
          </div>
          {subjects.map((subject) => {
            const tests = chartData.subjectTests[subject] || [];
            return (
              <div className="chart-card large" key={subject}>
                <div className="chart-header">
                  <h3>📝 {subject} - Tests</h3>
                  <div className="chart-value">
                    {tests.length} {tests.length === 1 ? "Test" : "Tests"}
                  </div>
                </div>
                <div className="chart-container small">
                  <Bar
                    data={{
                      labels: tests.map((t) => t.testName),
                      datasets: [
                        {
                          label: "Percentage",
                          data: tests.map((t) => formatNumber(t.percentage)),
                          backgroundColor: "rgba(102,126,234,0.85)",
                          borderRadius: 8,
                        },
                      ],
                    }}
                    options={{
                      ...commonBarOptions,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (scenario.isOnlyPeriod && chartData.type === "levelAnalysis") {
      return (
        <div className="charts-grid">
          <div className="chart-card fw">
            <div className="chart-header">
              <h3>📈 Average Percentage by Level</h3>
              <div className="chart-value">School Performance Overview</div>
            </div>
            <div className="chart-container">
              <Bar
                data={{
                  labels: (chartData.avgByLevel || []).map((i) => i.level),
                  datasets: [
                    {
                      label: "Average Percentage",
                      data: (chartData.avgByLevel || []).map(
                        (i) => i.avgPercentage
                      ),
                      backgroundColor: "rgba(102,126,234,0.8)",
                      borderColor: "rgba(102,126,234,1)",
                      borderWidth: 2,
                      borderRadius: 8,
                    },
                  ],
                }}
                options={{
                  ...commonBarOptions,
                  onClick: createChartClickHandler("level"),
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    if (scenario.isPeriodAndLevel && chartData.type === "subjectAnalysis") {
      return (
        <div className="charts-grid">
          <div className="chart-card fw">
            <div className="chart-header">
              <h3>📊 Average Percentage by Subject</h3>
              <div className="chart-value">Subject Performance Analysis</div>
            </div>
            <div className="chart-container">
              <Bar
                data={{
                  labels: (chartData.avgBySubject || []).map((i) => i.subject),
                  datasets: [
                    {
                      label: "Average Percentage",
                      data: (chartData.avgBySubject || []).map(
                        (i) => i.avgPercentage
                      ),
                      backgroundColor: "rgba(250,112,154,0.8)",
                      borderColor: "rgba(250,112,154,1)",
                      borderWidth: 2,
                      borderRadius: 8,
                    },
                  ],
                }}
                options={{
                  ...commonBarOptions,
                  onClick: createChartClickHandler("subject"),
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    if (scenario.isPeriodLevelAndSubject && chartData.type === "testAnalysis") {
      return (
        <div className="charts-grid">
          <div className="chart-card fw">
            <div className="chart-header">
              <h3>📈 Average Percentage by Tests</h3>
              <div className="chart-value">Test Performance Analysis</div>
            </div>
            <div className="chart-container">
              <Bar
                data={{
                  labels: (chartData.avgByTest || []).map((i) => i.testName),
                  datasets: [
                    {
                      label: "Average Percentage",
                      data: (chartData.avgByTest || []).map(
                        (i) => i.avgPercentage
                      ),
                      backgroundColor: "rgba(139,92,246,0.8)",
                      borderColor: "rgba(139,92,246,1)",
                      borderWidth: 2,
                      borderRadius: 8,
                    },
                  ],
                }}
                options={{
                  ...commonBarOptions,
                  onClick: createChartClickHandler("test"),
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    if (
      scenario.isPeriodLevelClassAndTest &&
      chartData.type === "studentScores"
    ) {
      return (
        <div className="charts-grid">
          <div className="chart-card fw">
            <div className="chart-header">
              <h3>👥 Percentage Score by Student</h3>
              <div className="chart-value">Individual Student Performance</div>
            </div>
            <div className="chart-container">
              <Bar
                data={{
                  labels: Object.keys(chartData.scoresByStudent || {}),
                  datasets: [
                    {
                      label: "Percentage",
                      data: Object.values(chartData.scoresByStudent || {}),
                      backgroundColor: Object.values(
                        chartData.scoresByStudent || {}
                      ).map((score) =>
                        score >= 80
                          ? "rgba(34,197,94,0.8)"
                          : score >= 60
                          ? "rgba(234,179,8,0.8)"
                          : "rgba(239,68,68,0.8)"
                      ),
                      borderRadius: 8,
                    },
                  ],
                }}
                options={{
                  ...commonBarOptions,
                  onClick: (event, activeElements) => {
                    if (activeElements.length > 0) {
                      const clickedIndex = activeElements[0].index;
                      const studentName = Object.keys(
                        chartData.scoresByStudent || {}
                      )[clickedIndex];

                      if (studentName && dashboardData?.classes) {
                        // Find student by matching name across all classes
                        let foundStudent = null;

                        // First try to find in the current filtered class
                        const currentClass = dashboardData.classes.find(
                          (cls) => cls._id === filters.classId
                        );
                        if (currentClass) {
                          foundStudent = (currentClass.students || []).find(
                            (s) =>
                              `${s.firstName} ${s.lastName}`.trim() ===
                              studentName.trim()
                          );
                        }

                        // If not found in current class, search all classes
                        if (!foundStudent) {
                          for (const cls of dashboardData.classes) {
                            foundStudent = (cls.students || []).find(
                              (s) =>
                                `${s.firstName} ${s.lastName}`.trim() ===
                                studentName.trim()
                            );
                            if (foundStudent) break;
                          }
                        }

                        // Also try matching with students array as fallback
                        if (!foundStudent) {
                          foundStudent = students.find(
                            (s) =>
                              `${s.firstName} ${s.lastName}`.trim() ===
                              studentName.trim()
                          );
                        }

                        if (foundStudent) {
                          handleFilterChange("student", foundStudent._id);
                        } else {
                          console.warn(
                            "Could not find student with name:",
                            studentName
                          );
                        }
                      }
                    }
                  },
                  onHover: (event, activeElements) => {
                    event.native.target.style.cursor =
                      activeElements.length > 0 ? "pointer" : "default";
                  },
                  plugins: {
                    ...commonBarOptions.plugins,
                    tooltip: {
                      ...commonBarOptions.plugins.tooltip,
                      callbacks: {
                        ...commonBarOptions.plugins.tooltip.callbacks,
                        afterLabel: () => {
                          return ["", "💡 Click to filter by this student"];
                        },
                      },
                    },
                  },
                }}
              />
            </div>
          </div>

          {/* Add Question Level Insights Chart */}
          {questionStats && questionStats.length > 0 && (
            <div className="chart-card fw">
              <div className="chart-header">
                <h3>📊 Question Level Insights</h3>
                <div className="chart-value">
                  Performance Analysis by Question
                </div>
              </div>
              <div className="chart-container">
                {loadingQuestionStats ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "200px",
                    }}
                  >
                    <Loader style={{ left: "0" }} />
                  </div>
                ) : (
                  <Bar
                    data={{
                      labels: questionStats.map(
                        (stat) => `Q${stat.questionNumber}`
                      ),
                      datasets: [
                        {
                          label: "Performance %",
                          data: questionStats.map((stat) =>
                            formatNumber(stat.percentObtained)
                          ),
                          backgroundColor: questionStats.map((stat) =>
                            stat.percentObtained >= 80
                              ? "rgba(34,197,94,0.8)"
                              : stat.percentObtained >= 60
                              ? "rgba(99, 102, 241, 0.8)"
                              : stat.percentObtained >= 40
                              ? "rgba(234,179,8,0.8)"
                              : "rgba(239,68,68,0.8)"
                          ),
                          borderColor: questionStats.map((stat) =>
                            stat.percentObtained >= 80
                              ? "rgba(34,197,94,1)"
                              : stat.percentObtained >= 60
                              ? "rgba(99, 102, 241, 1)"
                              : stat.percentObtained >= 40
                              ? "rgba(234,179,8,1)"
                              : "rgba(239,68,68,1)"
                          ),
                          borderWidth: 2,
                          borderRadius: 8,
                          borderSkipped: false,
                        },
                      ],
                    }}
                    options={{
                      ...questionStatsOptions,
                      onClick: (event, activeElements) => {
                        if (activeElements.length > 0) {
                          const clickedIndex = activeElements[0].index;
                          const questionNumber =
                            questionStats[clickedIndex].questionNumber;
                          const testId = filters.testId;

                          // Navigate to score-by-question page in new tab
                          window.open(
                            `/${role}/score-by-question/${testId}?question=${questionNumber}`,
                            "_blank"
                          );
                        }
                      },
                      onHover: (event, activeElements) => {
                        event.native.target.style.cursor =
                          activeElements.length > 0 ? "pointer" : "default";
                      },
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (scenario?.isStudentRole && chartData.type === "studentMulti") {
      const subjects = Object.keys(chartData.subjectTests || {});
      return (
        <div className="charts-grid">
          <div className="chart-card large">
            <div className="chart-header">
              <h3>📊 Your Performance - Average by Subject</h3>
              <div className="chart-value">Subject Performance Overview</div>
            </div>
            <div className="chart-container">
              <Bar
                data={{
                  labels: (chartData.avgBySubject || []).map((i) => i.subject),
                  datasets: [
                    {
                      label: "Average",
                      data: (chartData.avgBySubject || []).map(
                        (i) => i.avgPercentage
                      ),
                      backgroundColor: "rgba(52,211,153,0.8)",
                      borderColor: "rgba(52,211,153,1)",
                      borderWidth: 2,
                      borderRadius: 8,
                    },
                  ],
                }}
                options={{
                  ...commonBarOptions,
                  plugins: {
                    ...commonBarOptions.plugins,
                    tooltip: {
                      ...commonBarOptions.plugins.tooltip,
                      callbacks: {
                        ...commonBarOptions.plugins.tooltip.callbacks,
                        afterLabel: () => [], // Remove click hint for student
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
          {subjects.map((subject) => {
            const tests = chartData.subjectTests[subject] || [];
            return (
              <div className="chart-card large" key={subject}>
                <div className="chart-header">
                  <h3>📝 {subject} - Your Test Results</h3>
                  <div className="chart-value">
                    {tests.length} {tests.length === 1 ? "Test" : "Tests"}
                  </div>
                </div>
                <div className="chart-container small">
                  <Bar
                    data={{
                      labels: tests.map((t) => t.testName),
                      datasets: [
                        {
                          label: "Percentage",
                          data: tests.map((t) => formatNumber(t.percentage)),
                          backgroundColor: tests.map((t) =>
                            t.percentage >= 80
                              ? "rgba(34,197,94,0.85)"
                              : t.percentage >= 60
                              ? "rgba(102,126,234,0.85)"
                              : t.percentage >= 40
                              ? "rgba(234,179,8,0.85)"
                              : "rgba(239,68,68,0.85)"
                          ),
                          borderRadius: 8,
                        },
                      ],
                    }}
                    options={{
                      ...commonBarOptions,
                      onClick: (event, activeElements) => {
                        if (activeElements.length > 0) {
                          const clickedIndex = activeElements[0].index;
                          const testData = tests[clickedIndex];
                          if (testData && testData.testId) {
                            handleFilterChange("testId", testData.testId);
                          }
                        }
                      },
                      plugins: {
                        ...commonBarOptions.plugins,
                        tooltip: {
                          ...commonBarOptions.plugins.tooltip,
                          callbacks: {
                            ...commonBarOptions.plugins.tooltip.callbacks,
                            afterLabel: () => {
                              return ["", "💡 Click to view test details"];
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="charts-grid">
        No charts available for current filter selection.
      </div>
    );
  };

  const tableColumns = (() => {
    const baseColumns = [
      { key: "period", label: "Period", sortable: false },
      { key: "level", label: "Level", sortable: false },
      { key: "testType", label: "Test Type", sortable: false },
      { key: "className", label: "Class", sortable: false },
      { key: "instructor", label: "Instructor", sortable: false },
      { key: "testName", label: "Test", sortable: false },
      { key: "studentName", label: "Student", sortable: false },
      {
        key: "score",
        label: "Score",
        render: (item) =>
          typeof item.score === "number" ? (
            <a
              className="score-clickable"
              title="View Results"
              href={`/${role}/results/${item._id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color:
                  item.score / item.maxScore >= 0.8
                    ? "#16a34a"
                    : item.score / item.maxScore >= 0.6
                    ? "#eab308"
                    : "#dc2626",
                fontWeight: "bold",
                textDecoration: "none",
                cursor: "pointer",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {formatNumber(item.score)} / {item.maxScore}
            </a>
          ) : (
            <span style={{ color: "#6b7280", fontWeight: "bold" }}>
              {item.score}
            </span>
          ),
      },
      {
        key: "status",
        label: "Status",
        sortable: false,
        render: (item) => (
          <span
            className={`status-badge ${String(
              item.status || ""
            ).toLowerCase()}`}
          >
            {item.status}
          </span>
        ),
      },
    ];

    // Add download column only when Level>Class>Test is selected
    if (isSpecificTestSelected) {
      baseColumns.push({
        key: "download",
        label: "Download",
        render: (item) => {
          if (typeof item.score !== "number") return <span>-</span>;

          const studentData = {
            _id: item._id,
            firstName: item.studentName.split(" ")[0] || "",
            lastName: item.studentName.split(" ").slice(1).join(" ") || "",
            referenceId: item.referenceId || item.studentId || "", // Use the referenceId field
            testName: item.testName,
            className: item.className,
            classLevel: item.level,
          };

          return (
            <button
              className="delete-btn"
              disabled={
                activeAction.id === item._id && activeAction.type === "download"
              }
              onClick={() => handleDownloadSingle(studentData)}
              title={
                activeAction.id === item._id && activeAction.type === "download"
                  ? "Preparing..."
                  : "Download student's report"
              }
            >
              {activeAction.id === item._id &&
              activeAction.type === "download" ? (
                <Loader style={{ left: "0" }} />
              ) : (
                <i
                  className="fa-solid fa-download"
                  style={{ fontSize: "1.5rem" }}
                />
              )}
            </button>
          );
        },
        sortable: false,
      });
    }

    return baseColumns;
  })();

  if (loading && !dashboardData) {
    return (
      <div className="dashboard">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Error Loading Dashboard</h2>
          <p>{error}</p>
          <button
            onClick={() => fetchDashboardData(filters.period)}
            className="retry-btn"
          >
            <span>🔄</span> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <motion.div
        className="dashboard-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="header-content">
          <h1>📊 Analytics Dashboard</h1>
          <p>Comprehensive insights and data visualization</p>
        </div>
      </motion.div>

      <motion.div
        className="filters-panel"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="filters-header">
          <h3>🎯 Smart Filters</h3>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            {hasActiveFilters && (
              <button onClick={clearAllFilters} className="clear-filters-btn">
                Clear All
              </button>
            )}
          </div>
        </div>

        <div className="filters-grid">
          {!disableFilters.period && (
            <div className="filter-group">
              <label>📅 Period</label>
              <select
                value={filters.period}
                onChange={(e) => handleFilterChange("period", e.target.value)}
              >
                <option value="all">All Periods</option>
                {(periods || []).map((period) => (
                  <option key={period._id} value={period._id}>
                    {periodDisplayMap[period._id] || period._id}
                    {period.status === "Ongoing" && " (Ongoing)"}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!disableFilters.level && (
            <div className="filter-group">
              <label>🎓 Level</label>
              <select
                value={filters.level}
                onChange={(e) => handleFilterChange("level", e.target.value)}
                disabled={(filterOptions.levels || []).length === 0}
              >
                <option value="all">All Levels</option>
                {(filterOptions.levels || []).map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!disableFilters.class && (
            <div className="filter-group">
              <label>📚 Class</label>
              <select
                value={filters.classId}
                onChange={(e) => handleFilterChange("classId", e.target.value)}
                disabled={
                  filters.student !== "all"
                    ? (studentAvailable.classes || []).filter(
                        (c) =>
                          filters.testType === "all" ||
                          (c.tests || []).some(
                            (t) => t.testType === filters.testType
                          )
                      ).length === 0
                    : (filterOptions.classNames || []).length === 0
                }
              >
                <option value="all">All Classes</option>
                {(filters.student !== "all"
                  ? (studentAvailable.classes || [])
                      .filter(
                        (c) =>
                          filters.testType === "all" ||
                          (c.tests || []).some(
                            (t) => t.testType === filters.testType
                          )
                      )
                      .map((cls) => ({
                        id: cls._id,
                        name: cls.name,
                        level: cls.level,
                      }))
                  : filterOptions.classNames
                ).map((cls) => (
                  <option key={cls.id || cls._id} value={cls.id || cls._id}>
                    {cls.name} {cls.level ? `(${cls.level})` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!disableFilters.testType &&
            (filterOptions.testTypes || []).length > 0 && (
              <div className="filter-group">
                <label>🏷️ Test Type</label>
                <select
                  value={filters.testType}
                  onChange={(e) =>
                    handleFilterChange("testType", e.target.value)
                  }
                  disabled={(filterOptions.testTypes || []).length === 0}
                >
                  <option value="all">All Test Types</option>
                  {(filterOptions.testTypes || []).map((tt) => (
                    <option key={tt} value={tt}>
                      {tt}
                    </option>
                  ))}
                </select>
              </div>
            )}

          {!disableFilters.test && (
            <div className="filter-group">
              <label>📝 Test Name</label>
              <select
                value={filters.testId}
                onChange={(e) => handleFilterChange("testId", e.target.value)}
                disabled={(filterOptions.testNames || []).length === 0}
              >
                <option value="all">All Tests</option>
                {(filterOptions.testNames || []).map((test) => {
                  let className = "";
                  if (dashboardData && dashboardData.classes) {
                    const foundClass = dashboardData.classes.find((cls) =>
                      (cls.tests || []).some((t) => t._id === test.id)
                    );
                    if (foundClass) {
                      className = foundClass.name;
                    }
                  }
                  return (
                    <option key={test.id} value={test.id}>
                      {test.name}
                      {className ? ` (${className})` : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {!disableFilters.instructor && (
            <div className="filter-group">
              <label>👨‍🏫 Instructor</label>
              <select
                value={filters.instructorId}
                onChange={(e) =>
                  handleFilterChange("instructorId", e.target.value)
                }
                disabled={(filterOptions.instructors || []).length === 0}
              >
                <option value="all">All Instructors</option>
                {(filterOptions.instructors || []).map((inst) => (
                  <option key={inst.id} value={inst.id}>
                    {inst.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!disableFilters.student && (
            <div className="filter-group">
              <label>👤 Student</label>
              <select
                value={filters.student}
                onChange={(e) => handleFilterChange("student", e.target.value)}
                disabled={(filterOptions.students || []).length === 0}
              >
                <option value="all">All Students</option>
                {(filterOptions.students || []).map((student) => (
                  <option key={student._id} value={student._id}>
                    {student.firstName} {student.lastName}
                    {student.referenceId && student.referenceId !== student._id
                      ? ` (${student.referenceId})`
                      : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </motion.div>

      {/* Loading indicator for data refresh (e.g., period change) */}
      <AnimatePresence>
        {loading && dashboardData && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.75rem",
              padding: "1rem 1.5rem",
              margin: "0 0 1rem 0",
              background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
              borderRadius: "12px",
              border: "1px solid #bae6fd",
            }}
          >
            <Loader style={{ left: "0" }} />
            <p
              style={{
                color: "#0369a1",
                fontSize: "0.95rem",
                fontWeight: 500,
                margin: 0,
              }}
            >
              Updating dashboard data...
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {analytics && (
        <AnimatePresence mode="wait">
          <motion.div
            key={filters.period + JSON.stringify(filters)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              className="kpi-grid"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {renderKPICards()}
            </motion.div>

            <motion.div
              className="charts-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {renderCharts()}
            </motion.div>

            <motion.div
              className="data-table-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              {/* Add Download Reports button when Level>Class>Test is selected */}
              {isSpecificTestSelected && gradedStudentsForTest.length > 0 && (
                <div className="action-buttons">
                  <button
                    onClick={handleDownloadAllWithPrompt}
                    className="download-all-btn"
                    disabled={isDownloading}
                    title="Download all student reports for this test"
                  >
                    {isDownloading ? (
                      <>
                        <Loader
                          style={{ fontSize: "1rem", left: "0", color: "#fff" }}
                        />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-download" />
                        &nbsp; Download All Reports (
                        {gradedStudentsForTest.length})
                      </>
                    )}
                  </button>
                </div>
              )}
              <Table
                ref={tableRef}
                data={tableData}
                columns={tableColumns}
                caption={`Test Submissions ${
                  hasActiveFilters ? "(Filtered)" : ""
                } - ${tableData.length} Records`}
                onFilterChange={handleTableFilterChange}
                searchable
                exportable
              />
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}

      {/* Download Modal */}
      <Modal
        isOpen={isDownloadModalOpen}
        onClose={() => {
          if (!isDownloading) {
            setIsDownloadModalOpen(false);
          }
        }}
        maxWidth="620px"
      >
        <div style={{ padding: "1rem 0" }}>
          <h2
            style={{
              marginBottom: "1.5rem",
              fontSize: "1.5rem",
              fontWeight: "600",
              color: "#1f2937",
              textAlign: "center",
            }}
          >
            <i
              className="fa-solid fa-download"
              style={{ marginRight: "0.5rem", color: "var(--color-1)" }}
            ></i>
            Download Reports
          </h2>

          {!isDownloading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <div style={{ width: "100%", maxWidth: "480px" }}>
                <div className="form-group" style={{ marginBottom: "0.75rem" }}>
                  <label className="solution-toggle">
                    <input
                      type="checkbox"
                      name="downloadWithAnswerPaper"
                      checked={downloadWithAnswerPaper}
                      onChange={() =>
                        setDownloadWithAnswerPaper((prev) => !prev)
                      }
                    />
                    <span className="toggle-track">
                      <span className="toggle-indicator"></span>
                    </span>
                    &nbsp;Download&nbsp;
                    <span className="toggle-label-text">
                      {downloadWithAnswerPaper
                        ? "With Answer Papers"
                        : "Without Answer Papers"}
                    </span>
                  </label>
                </div>
              </div>
              <button
                onClick={handleDownloadAllWithPrompt}
                className="publish-action-btn"
                title="Download all student reports as individual PDF files"
              >
                <i
                  className="fa-solid fa-download"
                  style={{ marginRight: "8px" }}
                ></i>
                Download All ({gradedStudentsForTest.length} Reports)
              </button>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "1.5rem",
                padding: "2rem",
                borderRadius: "12px",
                background:
                  downloadPhase === "preparing"
                    ? "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"
                    : downloadPhase === "generating"
                    ? "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)"
                    : downloadPhase === "downloading"
                    ? "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)"
                    : downloadPhase === "completed"
                    ? "linear-gradient(135deg, #e8f5e8 0%, #a5d6a7 100%)"
                    : "#f8f9fa",
                border: `2px solid ${
                  downloadPhase === "preparing"
                    ? "#2196f3"
                    : downloadPhase === "generating"
                    ? "#ff9800"
                    : downloadPhase === "downloading"
                    ? "#9c27b0"
                    : downloadPhase === "completed"
                    ? "#2e7d32"
                    : "#e0e0e0"
                }`,
                transition: "all 0.5s ease-in-out",
              }}
            >
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {downloadPhase !== "completed" && (
                  <Loader
                    fontSize="50px"
                    style={{
                      left: "0",
                      color:
                        downloadPhase === "preparing"
                          ? "#1976d2"
                          : downloadPhase === "generating"
                          ? "#f57c00"
                          : downloadPhase === "downloading"
                          ? "#7b1fa2"
                          : "#666",
                    }}
                  />
                )}
                {downloadPhase === "completed" && (
                  <div style={{ fontSize: "50px", color: "#2e7d32" }}>
                    <i className="fa-solid fa-check-circle"></i>
                  </div>
                )}
              </div>

              <div
                style={{
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  color:
                    downloadPhase === "preparing"
                      ? "#1565c0"
                      : downloadPhase === "generating"
                      ? "#f57c00"
                      : downloadPhase === "downloading"
                      ? "#7b1fa2"
                      : downloadPhase === "completed"
                      ? "#1b5e20"
                      : "#1e40af",
                  textAlign: "center",
                }}
              >
                {downloadProgress}
              </div>

              {currentStudentName && downloadPhase !== "completed" && (
                <div
                  style={{
                    fontSize: "1rem",
                    color: "#6b7280",
                    textAlign: "center",
                    fontWeight: "500",
                    padding: "0.5rem 1rem",
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    borderRadius: "8px",
                    border: "1px solid rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <i
                    className="fa-solid fa-user"
                    style={{ marginRight: "0.5rem" }}
                  ></i>
                  {currentStudentName}
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Dashboard;